import {
  AddShoppingCart,
  Add,
  AutoGraph,
  Block,
  Cancel,
  Category,
  Delete,
  Edit,
  EventRepeat,
  FileDownload,
  Groups,
  FilterList,
  History,
  Inventory2,
  OpenInNew,
  Payments,
  PictureAsPdf,
  People,
  Refresh,
  RequestQuote,
  Save,
  Search,
  Visibility,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import {
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardPanelSx,
} from "../components/layout/dashboardStyles";
import { PageLoadingState } from "../components/layout/PageLoadingState";
import { configManager } from "../data/managers/ConfigManager";
import {
  CreateInvoiceBillingReminderParams,
  CreateInvoiceQuoteParams,
  CreateInvoiceSaleParams,
  InvoiceBillingPeriod,
  InvoiceBillingReminderFilters,
  InvoiceBillingReminderModel,
  InvoiceBillingReminderStatusFilter,
  InvoiceCatalogItemModel,
  InvoiceCategoryModel,
  InvoiceCommissionRule,
  InvoiceCommissionType,
  InvoiceCustomerModel,
  InvoicePaymentModel,
  InvoiceQuoteCurrency,
  InvoiceQuoteModel,
  InvoiceSaleInputItem,
  InvoiceSaleModel,
  InvoiceSalesFilters,
  InvoiceSellerModel,
  InvoiceUsageQuoteModel,
} from "../data/models/Invoices/InvoiceModels";
import { useInvoices } from "./hooks/useInvoices";

export type InvoiceView =
  | "analytics"
  | "categories"
  | "items"
  | "customers"
  | "sellers"
  | "reminders"
  | "quotes"
  | "quote-history"
  | "new-sale"
  | "sales";

export type InvoiceViewGroup = InvoiceView[];

type SalesQuickStatusFilter = "collectable" | "all" | "paid";

type DraftLine = Omit<InvoiceSaleInputItem, "commissionRule"> & {
  localId: string;
  displayName: string;
  commissionRule: InvoiceCommissionRule;
};

const VIEW_PATHS: Record<InvoiceView, string> = {
  analytics: "/dashboard/invoices/analytics",
  categories: "/dashboard/invoices/categories",
  items: "/dashboard/invoices/items",
  customers: "/dashboard/invoices/customers",
  sellers: "/dashboard/invoices/sellers",
  reminders: "/dashboard/invoices/reminders",
  quotes: "/dashboard/invoices/quotes",
  "quote-history": "/dashboard/invoices/quote-history",
  "new-sale": "/dashboard/invoices/new-sale",
  sales: "/dashboard/invoices/sales",
};

const blankCategory: Partial<InvoiceCategoryModel> = {
  name: "",
  description: "",
  active: true,
};

const blankItem: Partial<InvoiceCatalogItemModel> = {
  type: "service",
  name: "",
  categoryId: "",
  description: "",
  unitPrice: 0,
  isVariablePrice: false,
  cost: 0,
  active: true,
  commissionRule: {
    type: "none",
    value: 0,
    sellerId: "",
  },
  recurrence: {
    period: "none",
  },
  usagePricing: {
    enabled: false,
    pricePerChat: 0,
    pricePerMessage: 0,
    pricePerThousandTokens: 0,
  },
};

const blankCustomer: Partial<InvoiceCustomerModel> = {
  name: "",
  documentType: "CC",
  documentNumber: "",
  email: "",
  phone: "",
  address: "",
  lyproCompanyId: "",
  notes: "",
  active: true,
};

const blankSeller: Partial<InvoiceSellerModel> = {
  name: "",
  documentNumber: "",
  email: "",
  phone: "",
  notes: "",
  active: true,
};

const blankLine: Omit<DraftLine, "localId"> = {
  itemId: "",
  type: "service",
  name: "",
  displayName: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  cost: 0,
  sellerId: "",
  commissionRule: {
    type: "none",
    value: 0,
    sellerId: "",
  },
  recurrence: {
    period: "none",
  },
};

const blankBillingReminder: CreateInvoiceBillingReminderParams = {
  customerId: "",
  itemId: "",
  itemName: "",
  itemDescription: "",
  period: "yearly",
  quantity: 1,
  unitPrice: 0,
  cost: 0,
  renewalDate: "",
  notes: "",
};

const blankQuote: CreateInvoiceQuoteParams = {
  customerId: "",
  customer: blankCustomer,
  description: "",
  total: 0,
  currency: "COP",
  issuedAt: "",
};

const money = (value: number | undefined) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const quoteMoney = (
  value: number | undefined,
  currency: InvoiceQuoteCurrency = "COP"
) =>
  new Intl.NumberFormat(currency === "USD" ? "en-US" : "es-CO", {
    style: "currency",
    currency,
    minimumFractionDigits: currency === "USD" ? 2 : 0,
    maximumFractionDigits: currency === "USD" ? 2 : 0,
  }).format(Number(value || 0));

const dateLabel = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "America/Bogota",
      }).format(new Date(value))
    : "";

const formatCustomerDocument = (customer: InvoiceCustomerModel) =>
  [customer.documentType, customer.documentNumber].filter(Boolean).join(" ");

const dateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultIssueDate = () => dateInputValue(new Date());

const currentYearRange = () => {
  const now = new Date();
  return {
    from: dateInputValue(new Date(now.getFullYear(), 0, 1)),
    to: dateInputValue(now),
  };
};

const defaultBillingReminderForm = (): CreateInvoiceBillingReminderParams => ({
  ...blankBillingReminder,
  renewalDate: defaultIssueDate(),
});

const defaultQuoteForm = (): CreateInvoiceQuoteParams => ({
  ...blankQuote,
  customer: { ...blankCustomer },
  issuedAt: defaultIssueDate(),
});

const dateFromInputValue = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return new Date();
  }
  return new Date(year, month - 1, day);
};

const addMonthsClamped = (date: Date, months: number) => {
  const nextMonth = date.getMonth() + months;
  const lastDayOfTargetMonth = new Date(
    date.getFullYear(),
    nextMonth + 1,
    0
  ).getDate();
  return new Date(
    date.getFullYear(),
    nextMonth,
    Math.min(date.getDate(), lastDayOfTargetMonth)
  );
};

const defaultDueDate = (issuedDate = defaultIssueDate()) =>
  dateInputValue(addMonthsClamped(dateFromInputValue(issuedDate), 1));

const currentMonthRange = currentYearRange;

const defaultUsageMonth = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
  })
    .formatToParts(new Date())
    .reduce<Record<string, string>>((result, part) => {
      result[part.type] = part.value;
      return result;
    }, {});
  let year = Number(parts.year);
  let month = Number(parts.month) - 1;
  if (month === 0) {
    month = 12;
    year -= 1;
  }
  return `${year}-${String(month).padStart(2, "0")}`;
};

const salesFiltersFromValues = (
  status: SalesQuickStatusFilter,
  from: string,
  to: string
): InvoiceSalesFilters => ({
  status: status === "paid" ? "paid" : "all",
  from: from || undefined,
  to: to || undefined,
});

const numberValue = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

type SortDirection = "asc" | "desc";

type SortConfig<T extends string> = {
  orderBy: T;
  direction: SortDirection;
};

const normalizeSearchText = (value: unknown) =>
  value == null
    ? ""
    : String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const matchesSearchText = (value: unknown, query: string) =>
  !query || normalizeSearchText(value).includes(normalizeSearchText(query));

const compareSortableValues = (left: unknown, right: unknown) => {
  const leftNumber =
    typeof left === "number"
      ? left
      : left instanceof Date
        ? left.getTime()
        : Number.NaN;
  const rightNumber =
    typeof right === "number"
      ? right
      : right instanceof Date
        ? right.getTime()
        : Number.NaN;

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return normalizeSearchText(left).localeCompare(normalizeSearchText(right), "es", {
    numeric: true,
    sensitivity: "base",
  });
};

const sortTableRows = <T, K extends string>(
  rows: T[],
  sort: SortConfig<K>,
  accessors: Record<K, (row: T) => unknown>
) =>
  [...rows].sort((left, right) => {
    const result = compareSortableValues(
      accessors[sort.orderBy](left),
      accessors[sort.orderBy](right)
    );
    return sort.direction === "asc" ? result : -result;
  });

const usagePricingDefaults = {
  enabled: false,
  pricePerChat: 0,
  pricePerMessage: 0,
  pricePerThousandTokens: 0,
};

const renderSortableHeaderCell = <T extends string>(
  label: string,
  column: T,
  sort: SortConfig<T>,
  onSort: (column: T) => void,
  align: "left" | "right" | "center" = "left"
) => (
  <TableCell align={align}>
    <TableSortLabel
      active={sort.orderBy === column}
      direction={sort.orderBy === column ? sort.direction : "asc"}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const percent = (value: number | undefined) =>
  `${Number(value || 0).toLocaleString("es-CO", {
    maximumFractionDigits: 1,
  })}%`;

const statusLabel: Record<string, string> = {
  issued: "Por cobrar",
  partially_paid: "Abonada",
  paid: "Pagada",
  void: "Anulada",
};

const statusColor = (status: string) => {
  if (status === "paid") return "success";
  if (status === "partially_paid") return "warning";
  if (status === "void") return "error";
  return "default";
};

const salePaidTotal = (sale: InvoiceSaleModel) =>
  sale.status === "void" ? 0 : Number(sale.paidTotal || 0);

const saleBalance = (sale: InvoiceSaleModel) =>
  sale.status === "void" ? 0 : Number(sale.balance || 0);

const periodLabel: Record<InvoiceBillingPeriod, string> = {
  none: "No recurrente",
  monthly: "Mensual",
  quarterly: "Trimestral",
  yearly: "Anual",
};

const reminderStatusLabel: Record<string, string> = {
  pending: "Pendiente",
  billed: "Facturado",
  cancelled: "Cancelado",
};

const reminderStatusColor = (status: string) => {
  if (status === "billed") return "success";
  if (status === "cancelled") return "error";
  return "warning";
};

const normalizeCommissionType = (
  value: InvoiceCommissionType | undefined
): InvoiceCommissionType => value || "none";

const lineSubtotal = (line: Partial<InvoiceSaleInputItem>) =>
  Number(line.quantity || 0) * Number(line.unitPrice || 0);

const lineTotalCost = (line: Partial<InvoiceSaleInputItem>) =>
  Number(line.quantity || 0) * Number(line.cost || 0);

const lineCommissionAmount = (line: Partial<InvoiceSaleInputItem>) => {
  if (!line.sellerId && !line.commissionRule?.sellerId) return 0;
  const rule = line.commissionRule;
  if (rule?.type === "percentage") {
    return (lineSubtotal(line) * Number(rule.value || 0)) / 100;
  }
  if (rule?.type === "fixed") {
    return Number(line.quantity || 0) * Number(rule.value || 0);
  }
  return 0;
};

const lineProfit = (line: Partial<InvoiceSaleInputItem>) =>
  lineSubtotal(line) - lineTotalCost(line) - lineCommissionAmount(line);

type TablePaginationState = {
  page: number;
  rowsPerPage: number;
};

const defaultRowsPerPage = 5;

const useTablePagination = (rowsPerPageDefault = defaultRowsPerPage) => {
  const [pagination, setPagination] = React.useState<TablePaginationState>({
    page: 0,
    rowsPerPage: rowsPerPageDefault,
  });

  const setPage = React.useCallback((page: number) => {
    setPagination((current) => ({ ...current, page }));
  }, []);

  const setRowsPerPage = React.useCallback((rowsPerPage: number) => {
    setPagination({ page: 0, rowsPerPage });
  }, []);

  const resetPagination = React.useCallback(() => {
    setPagination((current) => ({ ...current, page: 0 }));
  }, []);

  return {
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    setPage,
    setRowsPerPage,
    resetPagination,
  };
};

const paginateRows = <T,>(
  rows: T[],
  page: number,
  rowsPerPage: number
): T[] => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

type ConfirmState = {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  onConfirm: () => void;
  confirmColor?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
};

const INVOICE_TABS: Array<{
  value: InvoiceView;
  label: string;
  icon: React.ReactElement;
}> = [
  { value: "analytics", label: "Analíticas", icon: <AutoGraph fontSize="small" /> },
  { value: "categories", label: "Categorías", icon: <Category fontSize="small" /> },
  { value: "items", label: "Productos/Servicios", icon: <Inventory2 fontSize="small" /> },
  { value: "customers", label: "Clientes", icon: <People fontSize="small" /> },
  { value: "sellers", label: "Vendedores", icon: <Groups fontSize="small" /> },
  { value: "reminders", label: "Recordatorios", icon: <EventRepeat fontSize="small" /> },
  { value: "quotes", label: "Cotizaciones", icon: <RequestQuote fontSize="small" /> },
  {
    value: "quote-history",
    label: "Historial cotizaciónes",
    icon: <History fontSize="small" />,
  },
  { value: "new-sale", label: "Nueva venta", icon: <AddShoppingCart fontSize="small" /> },
  { value: "sales", label: "Facturas/Cartera", icon: <Payments fontSize="small" /> },
];

const InvoicesPage = ({
  view,
  tabs,
}: {
  view: InvoiceView;
  tabs?: InvoiceViewGroup;
}) => {
  const navigate = useNavigate();
  const {
    catalog,
    lyproCompanies,
    quotes,
    sales,
    salesSummary,
    billingReminders,
    billingReminderSummary,
    loading,
    salesLoading,
    billingRemindersLoading,
    quotesLoading,
    saving,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarOpen,
    refreshSales,
    refreshBillingReminders,
    refreshQuotes,
    createCategory,
    updateCategory,
    deleteCategory,
    createItem,
    updateItem,
    deleteItem,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    createSeller,
    updateSeller,
    deleteSeller,
    createBillingReminder,
    updateBillingReminder,
    renewBillingReminder,
    cancelBillingReminder,
    deleteBillingReminder,
    createQuote,
    previewQuote,
    createSale,
    regenerateSalePdfs,
    addPayment,
    deletePayment,
    voidSale,
    getUsageQuote,
  } = useInvoices();

  const [confirmState, setConfirmState] = React.useState<ConfirmState | null>(
    null
  );
  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [categoryForm, setCategoryForm] =
    React.useState<Partial<InvoiceCategoryModel>>(blankCategory);
  const [editingCategoryId, setEditingCategoryId] = React.useState("");
  const [itemDialogOpen, setItemDialogOpen] = React.useState(false);
  const [itemForm, setItemForm] =
    React.useState<Partial<InvoiceCatalogItemModel>>(blankItem);
  const [editingItemId, setEditingItemId] = React.useState("");
  const [customerDialogOpen, setCustomerDialogOpen] = React.useState(false);
  const [customerForm, setCustomerForm] =
    React.useState<Partial<InvoiceCustomerModel>>(blankCustomer);
  const [editingCustomerId, setEditingCustomerId] = React.useState("");
  const [sellerDialogOpen, setSellerDialogOpen] = React.useState(false);
  const [sellerForm, setSellerForm] =
    React.useState<Partial<InvoiceSellerModel>>(blankSeller);
  const [editingSellerId, setEditingSellerId] = React.useState("");
  const [billingReminderDialogOpen, setBillingReminderDialogOpen] =
    React.useState(false);
  const [currentCompanyId, setCurrentCompanyId] = React.useState("");

  const [quoteForm, setQuoteForm] =
    React.useState<CreateInvoiceQuoteParams>(defaultQuoteForm);
  const [quoteFormDialogOpen, setQuoteFormDialogOpen] = React.useState(false);
  const [quoteSearch, setQuoteSearch] = React.useState("");
  const [quoteSort, setQuoteSort] = React.useState<
    SortConfig<"quoteNumber" | "customer" | "date" | "description" | "currency" | "total">
  >({
    orderBy: "date",
    direction: "desc",
  });
  const [quotePreviewHtml, setQuotePreviewHtml] = React.useState("");
  const [quotePreviewOpen, setQuotePreviewOpen] = React.useState(false);
  const [quoteHistorySearch, setQuoteHistorySearch] = React.useState("");
  const [quoteHistorySort, setQuoteHistorySort] = React.useState<
    SortConfig<"quoteNumber" | "customer" | "date" | "description" | "currency" | "total">
  >({
    orderBy: "date",
    direction: "desc",
  });

  const [saleCustomerId, setSaleCustomerId] = React.useState("");
  const [manualCustomer, setManualCustomer] =
    React.useState<Partial<InvoiceCustomerModel>>(blankCustomer);
  const [lineForm, setLineForm] = React.useState<DraftLine>({
    ...blankLine,
    localId: "",
  });
  const [saleLines, setSaleLines] = React.useState<DraftLine[]>([]);
  const [renewingBillingReminderId, setRenewingBillingReminderId] =
    React.useState("");
  const [renewingBillingReminderLabel, setRenewingBillingReminderLabel] =
    React.useState("");
  const [saleNotes, setSaleNotes] = React.useState("");
  const [saleIssuedAt, setSaleIssuedAt] = React.useState(defaultIssueDate);
  const [saleDueDate, setSaleDueDate] = React.useState(() => defaultDueDate());
  const [usageQuoteMonth, setUsageQuoteMonth] =
    React.useState(defaultUsageMonth);
  const [usageQuote, setUsageQuote] =
    React.useState<InvoiceUsageQuoteModel | null>(null);
  const [initialPayment, setInitialPayment] = React.useState({
    amount: 0,
    method: "",
    notes: "",
    paidAt: defaultIssueDate(),
  });
  const [saleFormDialogOpen, setSaleFormDialogOpen] = React.useState(false);
  const [categorySearch, setCategorySearch] = React.useState("");
  const [categorySort, setCategorySort] = React.useState<
    SortConfig<"name" | "description">
  >({
    orderBy: "name",
    direction: "asc",
  });
  const [itemSearch, setItemSearch] = React.useState("");
  const [itemSort, setItemSort] = React.useState<
    SortConfig<"name" | "category" | "type" | "period" | "price" | "cost">
  >({
    orderBy: "name",
    direction: "asc",
  });
  const [customerSearch, setCustomerSearch] = React.useState("");
  const [customerSort, setCustomerSort] = React.useState<
    SortConfig<"name" | "document" | "contact" | "address" | "lypro" | "notes">
  >({
    orderBy: "name",
    direction: "asc",
  });
  const [sellerSearch, setSellerSearch] = React.useState("");
  const [sellerSort, setSellerSort] = React.useState<
    SortConfig<"name" | "document" | "contact" | "notes">
  >({
    orderBy: "name",
    direction: "asc",
  });
  const [reminderSort, setReminderSort] = React.useState<
    SortConfig<
      "customer" | "service" | "reminder" | "renewal" | "period" | "total" | "invoice" | "status"
    >
  >({
    orderBy: "renewal",
    direction: "asc",
  });
  const [salesSort, setSalesSort] = React.useState<
    SortConfig<
      "invoiceNumber" | "customer" | "issuedAt" | "dueDate" | "status" | "total" | "paidTotal" | "balance"
    >
  >({
    orderBy: "issuedAt",
    direction: "desc",
  });
  const [analyticsDailySort, setAnalyticsDailySort] = React.useState<
    SortConfig<"date" | "invoiceCount" | "totalSales" | "paidTotal" | "balanceTotal" | "grossProfit">
  >({
    orderBy: "date",
    direction: "desc",
  });
  const [analyticsSellerSort, setAnalyticsSellerSort] = React.useState<
    SortConfig<"seller" | "invoiceCount" | "totalSales" | "commissionTotal" | "grossProfit">
  >({
    orderBy: "totalSales",
    direction: "desc",
  });
  const [analyticsItemSort, setAnalyticsItemSort] = React.useState<
    SortConfig<"item" | "category" | "quantity" | "totalSales" | "grossProfit">
  >({
    orderBy: "totalSales",
    direction: "desc",
  });

  const [selectedSale, setSelectedSale] =
    React.useState<InvoiceSaleModel | null>(null);
  const [paymentForm, setPaymentForm] =
    React.useState<Partial<InvoicePaymentModel>>({
      amount: 0,
      method: "",
      notes: "",
    });
  React.useEffect(() => {
    let active = true;

    void configManager.getAllConfig().then((data) => {
      if (!active) return;
      setCurrentCompanyId(data?.aiSettings?.companyId || "");
    });

    return () => {
      active = false;
    };
  }, []);

  const canUseLyproBilling = currentCompanyId === "Prueba-001";
  const lyproUsagePricingEnabled =
    canUseLyproBilling && Boolean(itemForm.usagePricing?.enabled);
  const [salesSearch, setSalesSearch] = React.useState("");
  const [salesStatus, setSalesStatus] =
    React.useState<SalesQuickStatusFilter>("all");
  const defaultSalesRange = React.useMemo(() => currentMonthRange(), []);
  const [salesFrom, setSalesFrom] = React.useState(defaultSalesRange.from);
  const [salesTo, setSalesTo] = React.useState(defaultSalesRange.to);
  const [appliedSalesFilters, setAppliedSalesFilters] =
    React.useState<InvoiceSalesFilters>(() =>
      salesFiltersFromValues("all", defaultSalesRange.from, defaultSalesRange.to)
    );
  const salesTablePagination = useTablePagination(10);
  const [billingReminderForm, setBillingReminderForm] =
    React.useState<CreateInvoiceBillingReminderParams>(
      defaultBillingReminderForm
    );
  const [editingBillingReminderId, setEditingBillingReminderId] =
    React.useState("");
  const [reminderSearch, setReminderSearch] = React.useState("");
  const [reminderStatus, setReminderStatus] =
    React.useState<InvoiceBillingReminderStatusFilter>("pending");
  const defaultReminderRange = React.useMemo(() => currentMonthRange(), []);
  const [reminderFrom, setReminderFrom] = React.useState(
    defaultReminderRange.from
  );
  const [reminderTo, setReminderTo] = React.useState(defaultReminderRange.to);
  const [appliedReminderFilters, setAppliedReminderFilters] =
    React.useState<InvoiceBillingReminderFilters>(() => ({
      status: "pending",
      from: defaultReminderRange.from,
      to: defaultReminderRange.to,
    }));
  const reminderTablePagination = useTablePagination(10);
  const categoryTablePagination = useTablePagination(10);
  const itemTablePagination = useTablePagination(10);
  const customerTablePagination = useTablePagination(10);
  const sellerTablePagination = useTablePagination(10);
  const quoteHistoryPagination = useTablePagination(10);
  const analyticsDailyPagination = useTablePagination(10);
  const analyticsSellerPagination = useTablePagination(10);
  const analyticsItemPagination = useTablePagination(10);

  const applySalesFilters = () => {
    setAppliedSalesFilters(
      salesFiltersFromValues(salesStatus, salesFrom, salesTo)
    );
    salesTablePagination.setPage(0);
  };

  const updateSalesStatus = (status: SalesQuickStatusFilter) => {
    setSalesStatus(status);
    setAppliedSalesFilters((current) =>
      salesFiltersFromValues(status, current.from || "", current.to || "")
    );
    salesTablePagination.setPage(0);
  };

  const handleSalesDateKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      applySalesFilters();
    }
  };

  const hasPendingSalesDateFilters =
    salesFrom !== (appliedSalesFilters.from || "") ||
    salesTo !== (appliedSalesFilters.to || "");

  const activeSalesStatus = salesStatus;

  React.useEffect(() => {
    if (view === "sales" || view === "analytics") {
      void refreshSales(appliedSalesFilters);
    }
  }, [appliedSalesFilters, refreshSales, view]);

  React.useEffect(() => {
    if (view === "quotes" || view === "quote-history") {
      void refreshQuotes();
    }
  }, [refreshQuotes, view]);

  const applyReminderFilters = () => {
    setAppliedReminderFilters({
      status: reminderStatus,
      from: reminderFrom || undefined,
      to: reminderTo || undefined,
    });
    reminderTablePagination.setPage(0);
  };

  const updateReminderStatus = (
    status: InvoiceBillingReminderStatusFilter
  ) => {
    setReminderStatus(status);
    setAppliedReminderFilters((current) => ({
      ...current,
      status,
    }));
    reminderTablePagination.setPage(0);
  };

  const handleReminderDateKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      applyReminderFilters();
    }
  };

  const hasPendingReminderDateFilters =
    reminderFrom !== (appliedReminderFilters.from || "") ||
    reminderTo !== (appliedReminderFilters.to || "");

  React.useEffect(() => {
    if (view === "reminders") {
      void refreshBillingReminders(appliedReminderFilters);
    }
  }, [appliedReminderFilters, refreshBillingReminders, view]);

  const lyproCompanyLabel = (companyId?: string) => {
    if (!companyId) return "";
    const company = lyproCompanies.find((entry) => entry.companyId === companyId);
    return company?.name || company?.companyId || companyId;
  };

  const selectedLineItem = catalog.items.find(
    (item) => item.id === lineForm.itemId
  );
  const lineHasUsagePricing = Boolean(
    selectedLineItem?.usagePricing?.enabled
  );
  const selectedSaleCustomer = catalog.customers.find(
    (customer) => customer.id === saleCustomerId
  );
  const saleCustomerHasLyproCompany = Boolean(
    selectedSaleCustomer?.lyproCompanyId
  );

  const clearLineUsage = () => {
    setUsageQuote(null);
    setLineForm((current) => ({
      ...current,
      usageBillingSnapshot: undefined,
    }));
  };

  const resetCategoryForm = () => {
    setCategoryForm(blankCategory);
    setEditingCategoryId("");
  };

  const resetItemForm = () => {
    setItemForm(blankItem);
    setEditingItemId("");
  };

  const resetCustomerForm = () => {
    setCustomerForm(blankCustomer);
    setEditingCustomerId("");
  };

  const resetSellerForm = () => {
    setSellerForm(blankSeller);
    setEditingSellerId("");
  };

  const resetQuoteForm = () => {
    setQuoteForm(defaultQuoteForm());
    setQuotePreviewHtml("");
    setQuotePreviewOpen(false);
  };

  const openQuoteCreateDialog = () => {
    resetQuoteForm();
    setQuoteFormDialogOpen(true);
  };

  const closeQuoteCreateDialog = () => {
    setQuoteFormDialogOpen(false);
    resetQuoteForm();
  };

  const resetBillingReminderForm = () => {
    setBillingReminderForm(defaultBillingReminderForm());
    setEditingBillingReminderId("");
  };

  const openBillingReminderCreateDialog = () => {
    resetBillingReminderForm();
    setBillingReminderDialogOpen(true);
  };

  const closeBillingReminderDialog = () => {
    setBillingReminderDialogOpen(false);
    resetBillingReminderForm();
  };

  const closeConfirmDialog = () => setConfirmState(null);

  const openConfirmDialog = (config: Omit<ConfirmState, "open">) => {
    setConfirmState({ ...config, open: true });
  };

  const openCategoryCreateDialog = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const openCategoryEditDialog = (category: InvoiceCategoryModel) => {
    setEditingCategoryId(category.id);
    setCategoryForm(category);
    setCategoryDialogOpen(true);
  };

  const closeCategoryDialog = () => {
    setCategoryDialogOpen(false);
    resetCategoryForm();
  };

  const openItemCreateDialog = () => {
    resetItemForm();
    setItemDialogOpen(true);
  };

  const openItemEditDialog = (item: InvoiceCatalogItemModel) => {
    setEditingItemId(item.id);
    setItemForm(item);
    setItemDialogOpen(true);
  };

  const closeItemDialog = () => {
    setItemDialogOpen(false);
    resetItemForm();
  };

  const openCustomerCreateDialog = () => {
    resetCustomerForm();
    setCustomerDialogOpen(true);
  };

  const openCustomerEditDialog = (customer: InvoiceCustomerModel) => {
    setEditingCustomerId(customer.id);
    setCustomerForm(customer);
    setCustomerDialogOpen(true);
  };

  const closeCustomerDialog = () => {
    setCustomerDialogOpen(false);
    resetCustomerForm();
  };

  const openSellerCreateDialog = () => {
    resetSellerForm();
    setSellerDialogOpen(true);
  };

  const openSellerEditDialog = (seller: InvoiceSellerModel) => {
    setEditingSellerId(seller.id);
    setSellerForm(seller);
    setSellerDialogOpen(true);
  };

  const closeSellerDialog = () => {
    setSellerDialogOpen(false);
    resetSellerForm();
  };

  const selectBillingReminderService = (itemId: string) => {
    const item = catalog.items.find((entry) => entry.id === itemId);
    if (!item) {
      setBillingReminderForm({
        ...billingReminderForm,
        itemId,
      });
      return;
    }
    setBillingReminderForm({
      ...billingReminderForm,
      itemId: item.id,
      itemName: item.name,
      itemDescription: item.description || "",
      period:
        item.recurrence?.period && item.recurrence.period !== "none"
          ? item.recurrence.period
          : "yearly",
      unitPrice: item.unitPrice,
      cost: item.cost,
    });
  };

  const editBillingReminder = (reminder: InvoiceBillingReminderModel) => {
    setEditingBillingReminderId(reminder.id);
    setBillingReminderForm({
      customerId: reminder.customerId || "",
      itemId: reminder.itemId || "",
      itemName: reminder.itemName,
      itemDescription: reminder.itemDescription || "",
      period: reminder.period,
      quantity: reminder.quantity,
      unitPrice: reminder.unitPrice,
      cost: reminder.cost,
      sellerId: reminder.sellerId,
      commissionRule: reminder.commissionRule,
      renewalDate: reminder.renewalDate.slice(0, 10),
      notes: reminder.notes || "",
    });
    setBillingReminderDialogOpen(true);
  };

  const updateSaleIssuedAt = (issuedAt: string) => {
    setSaleIssuedAt(issuedAt);
    setSaleDueDate(defaultDueDate(issuedAt));
    setInitialPayment((current) => ({
      ...current,
      paidAt: issuedAt,
    }));
  };

  const resetSaleForm = () => {
    const nextIssueDate = defaultIssueDate();
    setSaleCustomerId("");
    setManualCustomer(blankCustomer);
    setLineForm({ ...blankLine, localId: "" });
    setSaleLines([]);
    setSaleNotes("");
    setUsageQuoteMonth(defaultUsageMonth());
    setUsageQuote(null);
    setSaleIssuedAt(nextIssueDate);
    setSaleDueDate(defaultDueDate(nextIssueDate));
    setInitialPayment({
      amount: 0,
      method: "",
      notes: "",
      paidAt: nextIssueDate,
    });
    setRenewingBillingReminderId("");
    setRenewingBillingReminderLabel("");
  };

  const openNewSaleDialog = () => {
    resetSaleForm();
    setSaleFormDialogOpen(true);
  };

  const closeNewSaleDialog = () => {
    setSaleFormDialogOpen(false);
    resetSaleForm();
  };

  const selectItemForLine = (itemId: string) => {
    setUsageQuote(null);
    const item = catalog.items.find((entry) => entry.id === itemId);
    if (!item) {
      setLineForm({
        ...lineForm,
        itemId,
        usageBillingSnapshot: undefined,
      });
      return;
    }
    setLineForm({
      localId: "",
      itemId: item.id,
      type: item.type,
      name: item.name,
      displayName: item.name,
      description: item.description || "",
      quantity: 1,
      unitPrice: item.unitPrice,
      cost: item.cost,
      usageBillingSnapshot: undefined,
      sellerId: item.commissionRule?.sellerId || "",
      commissionRule: {
        type: normalizeCommissionType(item.commissionRule?.type),
        value: item.commissionRule?.value || 0,
        sellerId: item.commissionRule?.sellerId || "",
      },
      recurrence:
        item.type === "service"
          ? {
              period: item.recurrence?.period || "none",
            }
          : {
              period: "none",
            },
    });
  };

  const updateSaleCustomer = (customerId: string) => {
    setSaleCustomerId(customerId);
    clearLineUsage();
  };

  const applyUsageQuoteToLine = async () => {
    if (!saleCustomerId || !lineForm.itemId) return;
    const quote = await getUsageQuote({
      customerId: saleCustomerId,
      itemId: lineForm.itemId,
      month: usageQuoteMonth,
    });
    if (!quote) return;
    setUsageQuote(quote);
    setLineForm((current) => ({
      ...current,
      quantity: 1,
      unitPrice: quote.suggestedTotal,
      description: quote.description,
      usageBillingSnapshot: quote.snapshot,
    }));
  };

  const prepareSaleFromBillingReminder = (
    reminder: InvoiceBillingReminderModel
  ) => {
    const nextIssueDate = defaultIssueDate();
    setSaleCustomerId(reminder.customerId || "");
    setManualCustomer(
      reminder.customerId
        ? blankCustomer
        : {
            ...blankCustomer,
            ...reminder.customerSnapshot,
            active: true,
          }
    );
    setUsageQuote(null);
    setUsageQuoteMonth(defaultUsageMonth());
    const reminderLine: DraftLine = {
      localId: `${reminder.id}-${Date.now()}`,
      itemId: reminder.itemId || "",
      type: "service",
      name: reminder.itemName,
      displayName: reminder.itemName,
      description: reminder.itemDescription || "",
      quantity: Number(reminder.quantity || 1),
      unitPrice: Number(reminder.unitPrice || 0),
      cost: Number(reminder.cost || 0),
      sellerId: reminder.sellerId || "",
      commissionRule: {
        type: normalizeCommissionType(reminder.commissionRule?.type),
        value: Number(reminder.commissionRule?.value || 0),
        sellerId: reminder.sellerId || reminder.commissionRule?.sellerId,
      },
      recurrence: {
        period: reminder.period,
      },
    };
    const reminderItem = catalog.items.find(
      (item) => item.id === reminder.itemId
    );
    if (reminderItem?.usagePricing?.enabled) {
      setLineForm({
        ...reminderLine,
        localId: "",
      });
      setSaleLines([]);
    } else {
      setLineForm({ ...blankLine, localId: "" });
      setSaleLines([reminderLine]);
    }
    setSaleNotes(reminder.notes || `Renovacíon ${reminder.itemName}`);
    setSaleIssuedAt(nextIssueDate);
    setSaleDueDate(defaultDueDate(nextIssueDate));
    setInitialPayment({
      amount: 0,
      method: "",
      notes: "",
      paidAt: nextIssueDate,
    });
    setRenewingBillingReminderId(reminder.id);
    setRenewingBillingReminderLabel(
      `${reminder.customerSnapshot.name} - ${reminder.itemName}`
    );
    setSaleFormDialogOpen(true);
    navigate(VIEW_PATHS.sales);
  };

  const addLine = () => {
    const name = (lineForm.name || lineForm.displayName || "").trim();
    if (!name) return;
    setSaleLines((current) => [
      ...current,
      {
        ...lineForm,
        localId: `${Date.now()}-${Math.random()}`,
        name,
        displayName: name,
        quantity: Number(lineForm.quantity || 1),
        unitPrice: Number(lineForm.unitPrice || 0),
        cost: Number(lineForm.cost || 0),
        commissionRule: {
          type: normalizeCommissionType(lineForm.commissionRule?.type),
          value: Number(lineForm.commissionRule?.value || 0),
          sellerId: lineForm.sellerId || lineForm.commissionRule?.sellerId,
        },
        recurrence:
          lineForm.type === "service"
            ? {
                period: lineForm.recurrence?.period || "none",
              }
            : {
                period: "none",
              },
      },
    ]);
    setLineForm({ ...blankLine, localId: "" });
    setUsageQuote(null);
  };

  const updateDraftLine = (localId: string, patch: Partial<DraftLine>) => {
    setSaleLines((current) =>
      current.map((line): DraftLine => {
        if (line.localId !== localId) return line;
        const nextRule = {
          type: normalizeCommissionType(
            patch.commissionRule?.type || line.commissionRule?.type
          ),
          value:
            patch.commissionRule?.value ?? line.commissionRule?.value ?? 0,
          sellerId:
            patch.commissionRule?.sellerId ?? line.commissionRule?.sellerId,
        };
        return {
          ...line,
          ...patch,
          commissionRule: nextRule,
        };
      })
    );
  };

  const buildQuotePayload = (): CreateInvoiceQuoteParams => ({
    customerId: quoteForm.customerId || undefined,
    customer: quoteForm.customerId ? undefined : quoteForm.customer,
    description: quoteForm.description,
    total: Number(quoteForm.total || 0),
    currency: quoteForm.currency,
    issuedAt: quoteForm.issuedAt || undefined,
  });

  const saveCategory = async () => {
    const result = editingCategoryId
      ? await updateCategory(editingCategoryId, categoryForm)
      : await createCategory(categoryForm);
    if (result) {
      closeCategoryDialog();
    }
  };

  const saveItem = async () => {
    const result = editingItemId
      ? await updateItem(editingItemId, itemForm)
      : await createItem(itemForm);
    if (result) {
      closeItemDialog();
    }
  };

  const saveCustomer = async () => {
    const result = editingCustomerId
      ? await updateCustomer(editingCustomerId, customerForm)
      : await createCustomer(customerForm);
    if (result) {
      closeCustomerDialog();
    }
  };

  const saveSeller = async () => {
    const result = editingSellerId
      ? await updateSeller(editingSellerId, sellerForm)
      : await createSeller(sellerForm);
    if (result) {
      closeSellerDialog();
    }
  };

  const saveBillingReminder = async () => {
    const result = editingBillingReminderId
      ? await updateBillingReminder(
          editingBillingReminderId,
          billingReminderForm
        )
      : await createBillingReminder(billingReminderForm);
    if (result) {
      closeBillingReminderDialog();
    }
  };

  const requestCategorySave = () => {
    if (editingCategoryId) {
      openConfirmDialog({
        title: "Confirmar edición",
        message: "¿Deseas guardar los cambios de esta categoría?",
        confirmText: "Guardar",
        onConfirm: saveCategory,
      });
      return;
    }
    void saveCategory();
  };

  const requestItemSave = () => {
    if (editingItemId) {
      openConfirmDialog({
        title: "Confirmar edición",
        message: "¿Deseas guardar los cambios de este producto o servicio?",
        confirmText: "Guardar",
        onConfirm: saveItem,
      });
      return;
    }
    void saveItem();
  };

  const requestCustomerSave = () => {
    if (editingCustomerId) {
      openConfirmDialog({
        title: "Confirmar edición",
        message: "¿Deseas guardar los cambios de este cliente?",
        confirmText: "Guardar",
        onConfirm: saveCustomer,
      });
      return;
    }
    void saveCustomer();
  };

  const requestSellerSave = () => {
    if (editingSellerId) {
      openConfirmDialog({
        title: "Confirmar edición",
        message: "¿Deseas guardar los cambios de este vendedor?",
        confirmText: "Guardar",
        onConfirm: saveSeller,
      });
      return;
    }
    void saveSeller();
  };

  const requestBillingReminderSave = () => {
    if (editingBillingReminderId) {
      openConfirmDialog({
        title: "Confirmar edición",
        message: "¿Deseas guardar los cambios de este recordatorio?",
        confirmText: "Guardar",
        onConfirm: saveBillingReminder,
      });
      return;
    }
    void saveBillingReminder();
  };

  const previewQuoteDraft = async () => {
    const result = await previewQuote(buildQuotePayload());
    if (!result?.html) return;
    setQuotePreviewHtml(result.html);
    setQuotePreviewOpen(true);
  };

  const executeQuoteSubmission = async () => {
    const result = await createQuote(buildQuotePayload());
    if (!result) return;
    setQuoteFormDialogOpen(false);
    resetQuoteForm();
    if (result.pdfUrl) {
      window.open(result.pdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  const requestQuoteSubmission = () => {
    openConfirmDialog({
      title: "Generar cotización",
      message: "¿Deseas continuar y generar esta cotización?",
      confirmText: "Generar",
      onConfirm: executeQuoteSubmission,
    });
  };

  const executeSaleSubmission = async () => {
    if (saleLines.length === 0) return;
    const payload: CreateInvoiceSaleParams = {
      customerId: saleCustomerId || undefined,
      customer: saleCustomerId ? undefined : manualCustomer,
      items: saleLines.map(({ localId, displayName, ...line }) => ({
        ...line,
        name: line.name || displayName,
        commissionRule: {
          type: normalizeCommissionType(line.commissionRule?.type),
          value: Number(line.commissionRule?.value || 0),
          sellerId: line.sellerId || line.commissionRule?.sellerId,
        },
      })),
      issuedAt: saleIssuedAt || undefined,
      dueDate: saleDueDate || undefined,
      notes: saleNotes || undefined,
      initialPayment:
        Number(initialPayment.amount || 0) > 0
          ? {
              amount: Number(initialPayment.amount || 0),
              method: initialPayment.method,
              notes: initialPayment.notes,
              paidAt: initialPayment.paidAt || saleIssuedAt || undefined,
            }
          : undefined,
    };
    const result = renewingBillingReminderId
      ? await renewBillingReminder(renewingBillingReminderId, payload)
      : await createSale(payload);
    if (result) {
      setSaleFormDialogOpen(false);
      resetSaleForm();
      navigate(VIEW_PATHS.sales);
    }
  };

  const requestSaleSubmission = () => {
    openConfirmDialog({
      title: "Generar factura",
      message: "¿Deseas continuar y generar esta factura?",
      confirmText: "Generar",
      onConfirm: executeSaleSubmission,
    });
  };

  const submitQuote = requestQuoteSubmission;
  const submitSale = requestSaleSubmission;
  const submitBillingReminder = requestBillingReminderSave;

  const filteredSales = sales.filter((sale) => {
    const query = salesSearch.trim();
    const saleStatus = String(sale.status ?? "").trim().toLowerCase();
    const matchesQuery =
      !query ||
      matchesSearchText(sale.invoiceNumber, query) ||
      matchesSearchText(sale.customerSnapshot.name, query);
    if (!matchesQuery) {
      return false;
    }
    if (activeSalesStatus === "all") {
      return true;
    }
    if (activeSalesStatus === "collectable") {
      return saleStatus === "issued" || saleStatus === "partially_paid";
    }
    return saleStatus === "paid";
  });

  const visibleSalesSummary = React.useMemo(() => {
    const today = defaultIssueDate();
    const totals = filteredSales.reduce(
      (summary, sale) => {
        const balance = saleBalance(sale);
        const paidTotal = salePaidTotal(sale);
        const isPending =
          sale.status === "issued" || sale.status === "partially_paid";
        const isOverdue =
          isPending && Boolean(sale.dueDate) && sale.dueDate! < today && balance > 0;

        summary.invoiceCount += 1;
        summary.voidedInvoiceCount += sale.status === "void" ? 1 : 0;
        summary.totalSales += Number(sale.total || 0);
        summary.totalCost += Number(sale.totalCost || 0);
        summary.commissionTotal += Number(sale.commissionTotal || 0);
        summary.paidTotal += paidTotal;
        summary.balanceTotal += balance;
        summary.pendingInvoiceCount += isPending && balance > 0 ? 1 : 0;
        summary.overdueInvoiceCount += isOverdue ? 1 : 0;
        summary.overdueBalanceTotal += isOverdue ? balance : 0;

        return summary;
      },
      {
        invoiceCount: 0,
        voidedInvoiceCount: 0,
        totalSales: 0,
        totalCost: 0,
        commissionTotal: 0,
        paidTotal: 0,
        balanceTotal: 0,
        pendingInvoiceCount: 0,
        overdueInvoiceCount: 0,
        overdueBalanceTotal: 0,
      }
    );
    const grossProfit =
      totals.totalSales - totals.totalCost - totals.commissionTotal;
    const grossMarginPercent =
      totals.totalSales > 0 ? (grossProfit / totals.totalSales) * 100 : 0;

    return {
      ...totals,
      grossProfit,
      grossMarginPercent,
      averageTicket:
        totals.invoiceCount > 0 ? totals.totalSales / totals.invoiceCount : 0,
    };
  }, [filteredSales]);

  const filteredQuotes = quotes.filter((quote: InvoiceQuoteModel) => {
    const query = quoteSearch.trim();
    return (
      !query ||
      matchesSearchText(quote.quoteNumber, query) ||
      matchesSearchText(quote.customerSnapshot.name, query) ||
      matchesSearchText(quote.description, query)
    );
  });

  const filteredQuoteHistory = quotes.filter((quote: InvoiceQuoteModel) => {
    const query = quoteHistorySearch.trim();
    return (
      !query ||
      matchesSearchText(quote.quoteNumber, query) ||
      matchesSearchText(quote.customerSnapshot.name, query) ||
      matchesSearchText(quote.description, query)
    );
  });

  const filteredBillingReminders = billingReminders.filter((reminder) => {
    const query = reminderSearch.trim();
    return (
      !query ||
      matchesSearchText(reminder.itemName, query) ||
      matchesSearchText(reminder.customerSnapshot.name, query) ||
      matchesSearchText(reminder.sourceInvoiceNumber || "", query)
    );
  });

  const resetSalesFilters = () => {
    const monthRange = currentMonthRange();
    const nextFilters = salesFiltersFromValues(
      "all",
      monthRange.from,
      monthRange.to
    );
    setSalesSearch("");
    setSalesStatus("all");
    setSalesFrom(monthRange.from);
    setSalesTo(monthRange.to);
    setAppliedSalesFilters(nextFilters);
    salesTablePagination.setPage(0);
  };

  const resetReminderFilters = () => {
    const monthRange = currentMonthRange();
    setReminderSearch("");
    setReminderStatus("pending");
    setReminderFrom(monthRange.from);
    setReminderTo(monthRange.to);
    setAppliedReminderFilters({
      status: "pending",
      from: monthRange.from,
      to: monthRange.to,
    });
    reminderTablePagination.setPage(0);
  };

  const requestBillingReminderDelete = (id: string) => {
    openConfirmDialog({
      title: "Eliminar recordatorio",
      message: "¿Estás seguro de que deseas eliminar este recordatorio?",
      confirmText: "Eliminar",
      confirmColor: "error",
      onConfirm: async () => {
        await deleteBillingReminder(id);
      },
    });
  };

  const requestCategoryDelete = (id: string) => {
    openConfirmDialog({
      title: "Eliminar categoría",
      message: "¿Estás seguro de que deseas eliminar esta categoría?",
      confirmText: "Eliminar",
      confirmColor: "error",
      onConfirm: async () => {
        await deleteCategory(id);
      },
    });
  };

  const requestItemDelete = (id: string) => {
    openConfirmDialog({
      title: "Eliminar producto o servicio",
      message: "¿Estás seguro de que deseas eliminar este registro?",
      confirmText: "Eliminar",
      confirmColor: "error",
      onConfirm: async () => {
        await deleteItem(id);
      },
    });
  };

  const requestCustomerDelete = (id: string) => {
    openConfirmDialog({
      title: "Eliminar cliente",
      message: "¿Estás seguro de que deseas eliminar este cliente?",
      confirmText: "Eliminar",
      confirmColor: "error",
      onConfirm: async () => {
        await deleteCustomer(id);
      },
    });
  };

  const requestSellerDelete = (id: string) => {
    openConfirmDialog({
      title: "Eliminar vendedor",
      message: "¿Estás seguro de que deseas eliminar este vendedor?",
      confirmText: "Eliminar",
      confirmColor: "error",
      onConfirm: async () => {
        await deleteSeller(id);
      },
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({ amount: 0, method: "", notes: "" });
  };

  const closeSaleDialog = () => {
    setSelectedSale(null);
    resetPaymentForm();
  };

  const openSaleDialog = (sale: InvoiceSaleModel) => {
    setSelectedSale(sale);
    setPaymentForm({ amount: saleBalance(sale), method: "", notes: "" });
  };

  const registerSelectedSalePayment = async (amount?: number) => {
    if (!selectedSale) return;
    const result = await addPayment(selectedSale.id, {
      ...paymentForm,
      amount: amount ?? Number(paymentForm.amount || 0),
    });
    if (result) {
      closeSaleDialog();
    }
  };

  const renderMetricBox = (label: string, value: string, helper?: string) => (
    <Grid item xs={12} sm={6} md={3}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          p: 1.25,
          minHeight: 76,
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mt: 0.35 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Grid>
  );

  const renderSalesFilters = (includeSearch = true) => (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.25}
      sx={{
        mb: 1.5,
        p: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.default",
      }}
      alignItems={{ xs: "stretch", md: "center" }}
    >
      {includeSearch && (
        <TextField
          label="Buscar"
          size="small"
          value={salesSearch}
          onChange={(event) => {
            setSalesSearch(event.target.value);
            salesTablePagination.setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: "100%", md: 260 } }}
        />
      )}
      <TextField
        label="Desde"
        type="date"
        size="small"
        value={salesFrom}
        onChange={(event) => setSalesFrom(event.target.value)}
        onKeyDown={handleSalesDateKeyDown}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: { xs: "100%", md: 150 } }}
      />
      <TextField
        label="Hasta"
        type="date"
        size="small"
        value={salesTo}
        onChange={(event) => setSalesTo(event.target.value)}
        onKeyDown={handleSalesDateKeyDown}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: { xs: "100%", md: 150 } }}
      />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={salesStatus}
        onChange={(_event, nextStatus: SalesQuickStatusFilter | null) => {
          if (nextStatus) updateSalesStatus(nextStatus);
        }}
        sx={{
          alignSelf: { xs: "stretch", md: "center" },
          "& .MuiToggleButton-root": {
            px: 1.5,
            minHeight: 40,
            textTransform: "none",
          },
        }}
      >
        <ToggleButton value="collectable">Por cobrar</ToggleButton>
        <ToggleButton value="all">Todas</ToggleButton>
        <ToggleButton value="paid">Pagadas</ToggleButton>
      </ToggleButtonGroup>
      <Button
        variant={hasPendingSalesDateFilters ? "contained" : "outlined"}
        startIcon={<FilterList />}
        onClick={applySalesFilters}
        sx={{ minHeight: 40 }}
      >
        Aplicar
      </Button>
      <Button variant="text" onClick={resetSalesFilters} sx={{ minHeight: 40 }}>
        Limpiar
      </Button>
    </Stack>
  );

  const renderSummaryMetrics = () => (
    <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
      {renderMetricBox(
        "Vendido",
        money(visibleSalesSummary.totalSales),
        `${visibleSalesSummary.invoiceCount} facturas`
      )}
      {renderMetricBox("Abonado", money(visibleSalesSummary.paidTotal))}
      {renderMetricBox(
        "Saldo por cobrar",
        money(visibleSalesSummary.balanceTotal),
        `${visibleSalesSummary.pendingInvoiceCount} pendientes`
      )}
      {renderMetricBox(
        "Vencido",
        money(visibleSalesSummary.overdueBalanceTotal),
        `${visibleSalesSummary.overdueInvoiceCount} facturas`
      )}
      {renderMetricBox("Costo", money(visibleSalesSummary.totalCost))}
      {renderMetricBox("Comisiones", money(visibleSalesSummary.commissionTotal))}
      {renderMetricBox(
        "Ganancia",
        money(visibleSalesSummary.grossProfit),
        `Margen ${percent(visibleSalesSummary.grossMarginPercent)}`
      )}
      {renderMetricBox(
        "Ticket promedio",
        money(visibleSalesSummary.averageTicket),
        `${visibleSalesSummary.voidedInvoiceCount} anuladas`
      )}
    </Grid>
  );

  const renderReminderFilters = () => (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      sx={{ mb: 2 }}
      alignItems={{ xs: "stretch", md: "center" }}
    >
      <TextField
        label="Buscar"
        value={reminderSearch}
        onChange={(event) => setReminderSearch(event.target.value)}
        sx={{ minWidth: 240 }}
      />
      <TextField
        label="Desde aviso"
        type="date"
        value={reminderFrom}
        onChange={(event) => setReminderFrom(event.target.value)}
        onKeyDown={handleReminderDateKeyDown}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        label="Hasta aviso"
        type="date"
        value={reminderTo}
        onChange={(event) => setReminderTo(event.target.value)}
        onKeyDown={handleReminderDateKeyDown}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <FormControl sx={{ minWidth: 180 }}>
        <InputLabel>Estado</InputLabel>
        <Select
          label="Estado"
          value={reminderStatus}
          onChange={(event) =>
            updateReminderStatus(
              event.target.value as InvoiceBillingReminderStatusFilter
            )
          }
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="pending">Pendientes</MenuItem>
          <MenuItem value="billed">Facturados</MenuItem>
          <MenuItem value="cancelled">Cancelados</MenuItem>
        </Select>
      </FormControl>
      <Button
        variant={hasPendingReminderDateFilters ? "contained" : "outlined"}
        startIcon={<FilterList />}
        onClick={applyReminderFilters}
      >
        Aplicar
      </Button>
      <Button variant="outlined" onClick={resetReminderFilters}>
        Limpiar
      </Button>
    </Stack>
  );

  const renderReminderMetrics = () => (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {renderMetricBox(
        "Pendiente",
        money(billingReminderSummary.pendingTotal),
        `${billingReminderSummary.pendingCount} recordatorios`
      )}
      {renderMetricBox(
        "Vencido",
        money(billingReminderSummary.overduePendingTotal),
        `${billingReminderSummary.overduePendingCount} por revisar`
      )}
      {renderMetricBox(
        "Facturado",
        money(billingReminderSummary.billedTotal),
        `${billingReminderSummary.billedCount} renovaciónes`
      )}
      {renderMetricBox(
        "Total tabla",
        `${billingReminderSummary.reminderCount} registros`,
        `${billingReminderSummary.cancelledCount} cancelados`
      )}
    </Grid>
  );

  const visibleTabs = React.useMemo(
    () =>
      tabs
        ? tabs
            .map((value) => INVOICE_TABS.find((tab) => tab.value === value))
            .filter((tab): tab is (typeof INVOICE_TABS)[number] =>
              Boolean(tab)
            )
        : INVOICE_TABS,
    [tabs]
  );

  const renderHeader = () => (
    <Box sx={{ mb: 1.5, width: "100%" }}>
      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(_event, nextView: InvoiceView | null) => {
          if (nextView) navigate(VIEW_PATHS[nextView]);
        }}
        sx={{
          p: 0.5,
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: "background.paper",
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "8px !important",
            mx: 0,
          },
          "& .MuiToggleButton-root": {
            flex: "0 0 auto",
            px: { xs: 1.25, sm: 1.75 },
            minHeight: 38,
            textTransform: "none",
            gap: 0.75,
          },
        }}
      >
        {visibleTabs.map((tab) => (
          <ToggleButton
            key={tab.value}
            value={tab.value}
          >
            {tab.icon}
            {tab.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderItems = () => (
    <Stack spacing={2}>
      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Categorías
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              label="Nombre"
              fullWidth
              value={categoryForm.name || ""}
              onChange={(event) =>
                setCategoryForm({ ...categoryForm, name: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label="Descripción"
              fullWidth
              value={categoryForm.description || ""}
              onChange={(event) =>
                setCategoryForm({
                  ...categoryForm,
                  description: event.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={editingCategoryId ? <Save /> : <Add />}
              disabled={saving}
              onClick={async () => {
                const result = editingCategoryId
                  ? await updateCategory(editingCategoryId, categoryForm)
                  : await createCategory(categoryForm);
                if (result) resetCategoryForm();
              }}
              sx={{ height: "56px" }}
            >
              {editingCategoryId ? "Guardar" : "Crear"}
            </Button>
          </Grid>
        </Grid>
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catalog.categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.description}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingCategoryId(category.id);
                          setCategoryForm(category);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => deleteCategory(category.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Productos y servicios
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={itemForm.type || "service"}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    type: event.target.value as "product" | "service",
                    recurrence:
                      event.target.value === "service"
                        ? itemForm.recurrence || { period: "none" }
                        : { period: "none" },
                  })
                }
              >
                <MenuItem value="product">Producto</MenuItem>
                <MenuItem value="service">Servicio</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Nombre"
              fullWidth
              value={itemForm.name || ""}
              onChange={(event) =>
                setItemForm({ ...itemForm, name: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría"
                value={itemForm.categoryId || ""}
                onChange={(event) =>
                  setItemForm({ ...itemForm, categoryId: event.target.value })
                }
              >
                <MenuItem value="">Sin categoría</MenuItem>
                {catalog.categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Precio"
              type="number"
              fullWidth
              value={itemForm.unitPrice || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  unitPrice: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Costo"
              type="number"
              fullWidth
              value={itemForm.cost || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  cost: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={2}
              value={itemForm.description || ""}
              onChange={(event) =>
                setItemForm({ ...itemForm, description: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(itemForm.isVariablePrice)}
                  onChange={(event) =>
                    setItemForm({
                      ...itemForm,
                      isVariablePrice: event.target.checked,
                    })
                  }
                />
              }
              label="Precio variable"
            />
          </Grid>
          {itemForm.type !== "product" && (
            <>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(
                        itemForm.recurrence?.period &&
                          itemForm.recurrence.period !== "none"
                      )}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          recurrence: {
                            period: event.target.checked ? "yearly" : "none",
                          },
                        })
                      }
                    />
                  }
                  label="Servicio recurrente"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Periodicidad</InputLabel>
                  <Select
                    label="Periodicidad"
                    value={itemForm.recurrence?.period || "none"}
                    onChange={(event) =>
                      setItemForm({
                        ...itemForm,
                        recurrence: {
                          period: event.target.value as InvoiceBillingPeriod,
                        },
                      })
                    }
                  >
                    <MenuItem value="none">No recurrente</MenuItem>
                    <MenuItem value="monthly">Mensual</MenuItem>
                    <MenuItem value="quarterly">Trimestral</MenuItem>
                    <MenuItem value="yearly">Anual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {itemForm.type !== "product" && (
            <>
              {canUseLyproBilling ? (
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(itemForm.usagePricing?.enabled)}
                        onChange={(event) =>
                          setItemForm({
                            ...itemForm,
                            isVariablePrice:
                              event.target.checked || itemForm.isVariablePrice,
                            recurrence: event.target.checked
                              ? {
                                  period:
                                    itemForm.recurrence?.period &&
                                    itemForm.recurrence.period !== "none"
                                      ? itemForm.recurrence.period
                                      : "monthly",
                                }
                              : itemForm.recurrence,
                            usagePricing: {
                              ...(itemForm.usagePricing || usagePricingDefaults),
                              enabled: event.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Cobro LYPRO"
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      minHeight: 56,
                      display: "flex",
                      alignItems: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">
                      Cobro LYPRO solo disponible para Prueba-001.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {lyproUsagePricingEnabled && (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Precio chat"
                      type="number"
                      fullWidth
                      value={itemForm.usagePricing?.pricePerChat || 0}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerChat: numberValue(event.target.value),
                          },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Precio mensaje"
                      type="number"
                      fullWidth
                      value={itemForm.usagePricing?.pricePerMessage || 0}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerMessage: numberValue(event.target.value),
                          },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Precio 1K tokens"
                      type="number"
                      fullWidth
                      value={
                        itemForm.usagePricing?.pricePerThousandTokens || 0
                      }
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerThousandTokens: numberValue(
                              event.target.value
                            ),
                          },
                        })
                      }
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Comisión</InputLabel>
              <Select
                label="Comisión"
                value={itemForm.commissionRule?.type || "none"}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    commissionRule: {
                      ...(itemForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      type: event.target.value as InvoiceCommissionType,
                    },
                  })
                }
              >
                <MenuItem value="none">Sin comisión</MenuItem>
                <MenuItem value="percentage">Porcentaje</MenuItem>
                <MenuItem value="fixed">Valor fijo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Valor comisión"
              type="number"
              fullWidth
              value={itemForm.commissionRule?.value || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  commissionRule: {
                    ...(itemForm.commissionRule || {
                      type: "none",
                      value: 0,
                    }),
                    value: numberValue(event.target.value),
                  },
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Vendedor fijo</InputLabel>
              <Select
                label="Vendedor fijo"
                value={itemForm.commissionRule?.sellerId || ""}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    commissionRule: {
                      ...(itemForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      sellerId: event.target.value,
                    },
                  })
                }
              >
                <MenuItem value="">Sin vendedor fijo</MenuItem>
                {catalog.sellers.map((seller) => (
                  <MenuItem key={seller.id} value={seller.id}>
                    {seller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={editingItemId ? <Save /> : <Add />}
                disabled={saving}
                onClick={async () => {
                  const result = editingItemId
                    ? await updateItem(editingItemId, itemForm)
                    : await createItem(itemForm);
                  if (result) resetItemForm();
                }}
              >
                {editingItemId ? "Guardar" : "Crear"}
              </Button>
              {editingItemId && (
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={resetItemForm}
                >
                  Cancelar
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ítem</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Periodicidad</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Costo</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catalog.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                    {item.usagePricing?.enabled && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip size="small" label="LYPRO consumo" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{item.categoryName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.type === "product" ? "Producto" : "Servicio"}
                    />
                  </TableCell>
                  <TableCell>
                    {periodLabel[item.recurrence?.period || "none"]}
                  </TableCell>
                  <TableCell align="right">
                    {item.isVariablePrice ? "Variable" : money(item.unitPrice)}
                  </TableCell>
                  <TableCell align="right">{money(item.cost)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingItemId(item.id);
                          setItemForm(item);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => deleteItem(item.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );

  const renderPeopleForm = (
    kind: "customer" | "seller",
    form: any,
    editingId: string,
    setForm: React.Dispatch<React.SetStateAction<any>>,
    onSubmit: () => void | Promise<unknown>,
    onCancel: () => void,
    showActions = true,
    compact = false
  ) => (
    <Box sx={{ pt: compact ? 0 : 1 }}>
      {!compact && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {kind === "customer" ? "Clientes" : "Vendedores"}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={compact ? 6 : undefined} md={compact ? 6 : 3}>
          <TextField
            label="Nombre"
            size="medium"
            fullWidth
            value={form.name || ""}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            sx={peopleFieldSx}
          />
        </Grid>
        {kind === "customer" && (
          <Grid item xs={12} sm={compact ? 6 : undefined} md={compact ? 6 : 2}>
            <TextField
              label="Tipo doc."
              size="medium"
              fullWidth
              value={(form as InvoiceCustomerModel).documentType || ""}
              sx={peopleFieldSx}
              onChange={(event) =>
                setForm({ ...form, documentType: event.target.value })
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={compact ? 6 : undefined} md={compact ? 6 : 2}>
          <TextField
            label="Documento"
            size="medium"
            fullWidth
            value={form.documentNumber || ""}
            onChange={(event) =>
              setForm({ ...form, documentNumber: event.target.value })
            }
            sx={peopleFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={compact ? 6 : undefined} md={compact ? 6 : 2}>
          <TextField
            label="Teléfono"
            size="medium"
            fullWidth
            value={form.phone || ""}
            sx={peopleFieldSx}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={compact ? 6 : undefined} md={compact ? 6 : 3}>
          <TextField
            label="Correo"
            size="medium"
            fullWidth
            value={form.email || ""}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            sx={peopleFieldSx}
          />
        </Grid>
        {kind === "customer" && (
          <Grid item xs={12} sm={compact ? 6 : undefined} md={6}>
            <TextField
              label="Dirección"
              size="medium"
              fullWidth
              value={(form as InvoiceCustomerModel).address || ""}
              sx={peopleFieldSx}
              onChange={(event) =>
                setForm({ ...form, address: event.target.value })
              }
            />
          </Grid>
        )}
        {kind === "customer" && (
          <Grid item xs={12} sm={compact ? 6 : undefined} md={6}>
            <FormControl fullWidth size="medium" sx={peopleFieldSx}>
              <InputLabel>Compañía LYPRO</InputLabel>
              <Select
                label="Compañía LYPRO"
                value={(form as InvoiceCustomerModel).lyproCompanyId || ""}
                onChange={(event) =>
                  setForm({ ...form, lyproCompanyId: event.target.value })
                }
              >
                <MenuItem value="">Sin asociar</MenuItem>
                {lyproCompanies.map((company) => (
                  <MenuItem key={company.companyId} value={company.companyId}>
                    {company.name || company.companyId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={compact ? 12 : kind === "customer" ? 6 : 12}>
          <TextField
            label="Notas"
            size="medium"
            fullWidth
            value={form.notes || ""}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            sx={peopleFieldSx}
          />
        </Grid>
        {showActions && (
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={editingId ? <Save /> : <Add />}
                disabled={saving}
                onClick={onSubmit}
              >
                {editingId ? "Guardar" : "Crear"}
              </Button>
              {editingId && (
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={onCancel}
                >
                  Cancelar
                </Button>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const renderPeopleTable = (
    kind: "customer" | "seller",
    rows: Array<InvoiceCustomerModel | InvoiceSellerModel>,
    onEdit: (row: any) => void,
    onDelete: (id: string) => void,
    sort:
      | SortConfig<"name" | "document" | "contact" | "address" | "lypro" | "notes">
      | SortConfig<"name" | "document" | "contact" | "notes">,
    onSort: (column: string) => void
  ) => (
    <Box sx={panelSx}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {renderSortableHeaderCell("Nombre", "name" as any, sort as any, onSort)}
              {renderSortableHeaderCell(
                "Documento",
                "document" as any,
                sort as any,
                onSort
              )}
              {renderSortableHeaderCell(
                "Contacto",
                "contact" as any,
                sort as any,
                onSort
              )}
              {kind === "customer" && <TableCell>Dirección</TableCell>}
              {kind === "customer" &&
                renderSortableHeaderCell(
                  "LYPRO",
                  "lypro" as any,
                  sort as any,
                  onSort
                )}
              {renderSortableHeaderCell("Notas", "notes" as any, sort as any, onSort)}
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  {"documentType" in row && row.documentType
                    ? `${row.documentType} ${row.documentNumber || ""}`
                    : row.documentNumber}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.phone}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.email}
                  </Typography>
                </TableCell>
                {kind === "customer" && (
                  <TableCell>{(row as InvoiceCustomerModel).address}</TableCell>
                )}
                {kind === "customer" && (
                  <TableCell>
                    {lyproCompanyLabel(
                      (row as InvoiceCustomerModel).lyproCompanyId
                    ) || "-"}
                  </TableCell>
                )}
                <TableCell>{row.notes}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => onEdit(row)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => onDelete(row.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderCustomers = () => (
    <Stack spacing={2}>
      {renderPeopleForm(
        "customer",
        customerForm,
        editingCustomerId,
        setCustomerForm,
        async () => {
          const result = editingCustomerId
            ? await updateCustomer(editingCustomerId, customerForm)
            : await createCustomer(customerForm);
          if (result) resetCustomerForm();
        },
        resetCustomerForm,
        true
      )}
      {renderPeopleTable(
        "customer",
        catalog.customers,
        (row: InvoiceCustomerModel) => {
          setEditingCustomerId(row.id);
          setCustomerForm(row);
        },
        deleteCustomer,
        customerSort as any,
        (column) => {
          setCustomerSort((current) =>
            current.orderBy === (column as any)
              ? {
                  orderBy: current.orderBy,
                  direction: current.direction === "asc" ? "desc" : "asc",
                }
              : { orderBy: column as any, direction: "asc" }
          );
        }
      )}
    </Stack>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderSellers = () => (
    <Stack spacing={2}>
      {renderPeopleForm(
        "seller",
        sellerForm,
        editingSellerId,
        setSellerForm,
        async () => {
          const result = editingSellerId
            ? await updateSeller(editingSellerId, sellerForm)
            : await createSeller(sellerForm);
          if (result) resetSellerForm();
        },
        resetSellerForm,
        true
      )}
      {renderPeopleTable(
        "seller",
        catalog.sellers,
        (row: InvoiceSellerModel) => {
          setEditingSellerId(row.id);
          setSellerForm(row);
        },
        deleteSeller,
        sellerSort as any,
        (column) => {
          setSellerSort((current) =>
            current.orderBy === (column as any)
              ? {
                  orderBy: current.orderBy,
                  direction: current.direction === "asc" ? "desc" : "asc",
                }
              : { orderBy: column as any, direction: "asc" }
          );
        }
      )}
    </Stack>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderQuotes = () => {
    const manualCustomer: Partial<InvoiceCustomerModel> =
      quoteForm.customer || {};
    const canSubmitQuote =
      quoteForm.description.trim().length > 0 &&
      Number(quoteForm.total || 0) > 0 &&
      Boolean(quoteForm.customerId || manualCustomer.name);

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Cotización
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={quoteForm.customerId || ""}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      customerId: event.target.value,
                    })
                  }
                >
                  <MenuItem value="">Cliente manual</MenuItem>
                  {catalog.customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!quoteForm.customerId && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Nombre cliente"
                    fullWidth
                    value={manualCustomer.name || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          name: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Documento"
                    fullWidth
                    value={manualCustomer.documentNumber || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          documentNumber: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Teléfono"
                    fullWidth
                    value={manualCustomer.phone || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          phone: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Correo"
                    fullWidth
                    value={manualCustomer.email || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          email: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Dirección"
                    fullWidth
                    value={manualCustomer.address || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          address: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                label="Fecha"
                type="date"
                fullWidth
                value={quoteForm.issuedAt || ""}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    issuedAt: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: defaultIssueDate() }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select
                  label="Moneda"
                  value={quoteForm.currency}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      currency: event.target.value as InvoiceQuoteCurrency,
                    })
                  }
                >
                  <MenuItem value="COP">COP</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Total"
                type="number"
                fullWidth
                value={quoteForm.total}
                inputProps={{
                  min: 0,
                  step: quoteForm.currency === "USD" ? "0.01" : "1",
                }}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    total: numberValue(event.target.value),
                  })
                }
                helperText={quoteMoney(quoteForm.total, quoteForm.currency)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={6}
                value={quoteForm.description}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    description: event.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Visibility />}
                disabled={saving || !canSubmitQuote}
                onClick={previewQuoteDraft}
                sx={{ height: "56px" }}
              >
                Previsualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdf />}
                disabled={saving || !canSubmitQuote}
                onClick={submitQuote}
                sx={{ height: "56px" }}
              >
                Generar cotización
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Historial
            </Typography>
            <TextField
              label="Buscar"
              size="small"
              value={quoteSearch}
              onChange={(event) => setQuoteSearch(event.target.value)}
            />
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Cotización", "quoteNumber" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  })}
                  {renderSortableHeaderCell("Cliente", "customer" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  })}
                  {renderSortableHeaderCell("Fecha", "date" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  })}
                  {renderSortableHeaderCell("Descripción", "description" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  })}
                  {renderSortableHeaderCell("Moneda", "currency" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  })}
                  {renderSortableHeaderCell("Total", "total" as any, quoteSort as any, (column) => {
                    setQuoteSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                  }, "right")}
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortTableRows(filteredQuotes, quoteSort as any, {
                  quoteNumber: (quote: InvoiceQuoteModel) => quote.quoteNumber || "",
                  customer: (quote: InvoiceQuoteModel) => quote.customerSnapshot.name || "",
                  date: (quote: InvoiceQuoteModel) => quote.issuedAt || "",
                  description: (quote: InvoiceQuoteModel) => quote.description || "",
                  currency: (quote: InvoiceQuoteModel) => quote.currency || "",
                  total: (quote: InvoiceQuoteModel) => Number(quote.total || 0),
                } as any).map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell>{quote.quoteNumber}</TableCell>
                    <TableCell>{quote.customerSnapshot.name}</TableCell>
                    <TableCell>{dateLabel(quote.issuedAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {quote.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{quote.currency}</TableCell>
                    <TableCell align="right">
                      {quoteMoney(quote.total, quote.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {quote.pdfUrl && (
                        <Tooltip title="Cotización PDF">
                          <IconButton
                            size="small"
                            component="a"
                            href={quote.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    );
  };

  const renderNewSale = (inDialog = false) => (
    <Stack spacing={2}>
      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Venta
        </Typography>
        {renewingBillingReminderId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Factura desde recordatorio: {renewingBillingReminderLabel}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Cliente</InputLabel>
              <Select
                label="Cliente"
                value={saleCustomerId}
                onChange={(event) => updateSaleCustomer(event.target.value)}
              >
                <MenuItem value="">Cliente manual</MenuItem>
                {catalog.customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!saleCustomerId && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Nombre cliente"
                  fullWidth
                  value={manualCustomer.name || ""}
                  onChange={(event) =>
                    setManualCustomer({
                      ...manualCustomer,
                      name: event.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Documento"
                  fullWidth
                  value={manualCustomer.documentNumber || ""}
                  onChange={(event) =>
                    setManualCustomer({
                      ...manualCustomer,
                      documentNumber: event.target.value,
                    })
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={4}>
            <TextField
              label="Fecha de factura"
              type="date"
              fullWidth
              value={saleIssuedAt}
              onChange={(event) => updateSaleIssuedAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: defaultIssueDate() }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Vencimiento"
              type="date"
              fullWidth
              value={saleDueDate}
              onChange={(event) => setSaleDueDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Observacíones"
              fullWidth
              value={saleNotes}
              onChange={(event) => setSaleNotes(event.target.value)}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Ítems
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Producto/servicio</InputLabel>
              <Select
                label="Producto/servicio"
                value={lineForm.itemId || ""}
                onChange={(event) => selectItemForLine(event.target.value)}
              >
                <MenuItem value="">Manual</MenuItem>
                {catalog.items.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {lineHasUsagePricing && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Mes consumo"
                  type="month"
                  fullWidth
                  value={usageQuoteMonth}
                  onChange={(event) => {
                    setUsageQuoteMonth(event.target.value);
                    setUsageQuote(null);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Payments />}
                  disabled={
                    saving ||
                    !saleCustomerId ||
                    !saleCustomerHasLyproCompany ||
                    !lineForm.itemId
                  }
                  onClick={applyUsageQuoteToLine}
                  sx={{ height: "56px" }}
                >
                  Cotizar
                </Button>
              </Grid>
              <Grid item xs={12}>
                {usageQuote ? (
                  <Alert severity="info">
                    {usageQuote.periodMonth} - {usageQuote.lyproCompanyName} -
                    Chats {usageQuote.chats.toLocaleString("es-CO")} - Mensajes{" "}
                    {usageQuote.messages.toLocaleString("es-CO")} - Tokens{" "}
                    {usageQuote.tokens.toLocaleString("es-CO")} - Sugerido{" "}
                    {money(usageQuote.suggestedTotal)}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    Selecciona un cliente con compañía LYPRO y cotiza el mes.
                  </Alert>
                )}
              </Grid>
            </>
          )}
          {!lineForm.itemId && (
            <Grid item xs={12} md={4}>
              <TextField
                label="Nombre"
                fullWidth
                value={lineForm.name || ""}
                onChange={(event) =>
                  setLineForm({
                    ...lineForm,
                    name: event.target.value,
                    displayName: event.target.value,
                  })
                }
              />
            </Grid>
          )}
          <Grid item xs={6} md={2}>
            <TextField
              label="Cantidad"
              type="number"
              fullWidth
              value={lineForm.quantity || 1}
              onChange={(event) =>
                setLineForm({
                  ...lineForm,
                  quantity: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Precio"
              type="number"
              fullWidth
              value={lineForm.unitPrice || 0}
              onChange={(event) =>
                setLineForm({
                  ...lineForm,
                  unitPrice: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Costo"
              type="number"
              fullWidth
              value={lineForm.cost || 0}
              onChange={(event) =>
                setLineForm({
                  ...lineForm,
                  cost: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción en factura"
              fullWidth
              multiline
              rows={2}
              value={lineForm.description || ""}
              onChange={(event) =>
                setLineForm({ ...lineForm, description: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Vendedor</InputLabel>
              <Select
                label="Vendedor"
                value={lineForm.sellerId || ""}
                onChange={(event) =>
                  setLineForm({
                    ...lineForm,
                    sellerId: event.target.value,
                    commissionRule: {
                      ...(lineForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      sellerId: event.target.value,
                    },
                  })
                }
              >
                <MenuItem value="">Sin vendedor</MenuItem>
                {catalog.sellers.map((seller) => (
                  <MenuItem key={seller.id} value={seller.id}>
                    {seller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Comisión</InputLabel>
              <Select
                label="Comisión"
                value={lineForm.commissionRule?.type || "none"}
                onChange={(event) =>
                  setLineForm({
                    ...lineForm,
                    commissionRule: {
                      ...(lineForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      type: event.target.value as InvoiceCommissionType,
                    },
                  })
                }
              >
                <MenuItem value="none">Sin comisión</MenuItem>
                <MenuItem value="percentage">Porcentaje</MenuItem>
                <MenuItem value="fixed">Valor fijo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Valor comisión"
              type="number"
              fullWidth
              value={lineForm.commissionRule?.value || 0}
              onChange={(event) =>
                setLineForm({
                  ...lineForm,
                  commissionRule: {
                    ...(lineForm.commissionRule || {
                      type: "none",
                      value: 0,
                    }),
                    value: numberValue(event.target.value),
                  },
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Recurrencia</InputLabel>
              <Select
                label="Recurrencia"
                value={lineForm.recurrence?.period || "none"}
                onChange={(event) =>
                  setLineForm({
                    ...lineForm,
                    recurrence: {
                      period: event.target.value as InvoiceBillingPeriod,
                    },
                  })
                }
              >
                <MenuItem value="none">No recurrente</MenuItem>
                <MenuItem value="monthly">Mensual</MenuItem>
                <MenuItem value="quarterly">Trimestral</MenuItem>
                <MenuItem value="yearly">Anual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Add />}
              onClick={addLine}
              sx={{ height: "56px" }}
            >
              Agregar
            </Button>
          </Grid>
        </Grid>

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ítem</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Costo</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Comisión</TableCell>
                <TableCell>Recurrencia</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Ganancia</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {saleLines.map((line) => (
                <TableRow key={line.localId}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {line.name || line.displayName}
                    </Typography>
                    {line.usageBillingSnapshot && (
                      <Typography variant="caption" color="text.secondary">
                        LYPRO {line.usageBillingSnapshot.periodMonth}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={line.description || ""}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          description: event.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={line.quantity || 1}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          quantity: numberValue(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={line.unitPrice || 0}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          unitPrice: numberValue(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={line.cost || 0}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          cost: numberValue(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Select
                      size="small"
                      fullWidth
                      value={line.sellerId || ""}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          sellerId: event.target.value,
                          commissionRule: {
                            ...line.commissionRule,
                            sellerId: event.target.value,
                          },
                        })
                      }
                    >
                      <MenuItem value="">Sin vendedor</MenuItem>
                      {catalog.sellers.map((seller) => (
                        <MenuItem key={seller.id} value={seller.id}>
                          {seller.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ minWidth: 210 }}>
                    <Stack direction="row" spacing={1}>
                      <Select
                        size="small"
                        value={line.commissionRule?.type || "none"}
                        onChange={(event) =>
                          updateDraftLine(line.localId, {
                            commissionRule: {
                              ...line.commissionRule,
                              type: event.target.value as InvoiceCommissionType,
                            },
                          })
                        }
                        sx={{ minWidth: 110 }}
                      >
                        <MenuItem value="none">Ninguna</MenuItem>
                        <MenuItem value="percentage">%</MenuItem>
                        <MenuItem value="fixed">$</MenuItem>
                      </Select>
                      <TextField
                        size="small"
                        type="number"
                        value={line.commissionRule?.value || 0}
                        onChange={(event) =>
                          updateDraftLine(line.localId, {
                            commissionRule: {
                              ...line.commissionRule,
                              value: numberValue(event.target.value),
                            },
                          })
                        }
                      />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Select
                      size="small"
                      fullWidth
                      value={line.recurrence?.period || "none"}
                      onChange={(event) =>
                        updateDraftLine(line.localId, {
                          recurrence: {
                            period: event.target.value as InvoiceBillingPeriod,
                          },
                        })
                      }
                    >
                      <MenuItem value="none">No</MenuItem>
                      <MenuItem value="monthly">Mensual</MenuItem>
                      <MenuItem value="quarterly">Trimestral</MenuItem>
                      <MenuItem value="yearly">Anual</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    {money(lineSubtotal(line))}
                  </TableCell>
                  <TableCell align="right">
                    {money(lineProfit(line))}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSaleLines((current) =>
                          current.filter((entry) => entry.localId !== line.localId)
                        )
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={panelSx}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Abono inicial"
              type="number"
              fullWidth
              value={initialPayment.amount}
              onChange={(event) =>
                setInitialPayment({
                  ...initialPayment,
                  amount: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Método"
              fullWidth
              value={initialPayment.method}
              onChange={(event) =>
                setInitialPayment({
                  ...initialPayment,
                  method: event.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha abono"
              type="date"
              fullWidth
              value={initialPayment.paidAt}
              onChange={(event) =>
                setInitialPayment({
                  ...initialPayment,
                  paidAt: event.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Nota abono"
              fullWidth
              value={initialPayment.notes}
              onChange={(event) =>
                setInitialPayment({
                  ...initialPayment,
                  notes: event.target.value,
                })
              }
            />
          </Grid>
          {!inDialog && (
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdf />}
                disabled={saving || saleLines.length === 0}
                onClick={submitSale}
                sx={{ height: "56px" }}
              >
                Generar factura
              </Button>
            </Grid>
          )}
          {renewingBillingReminderId && (
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Cancel />}
                disabled={saving}
                onClick={resetSaleForm}
                sx={{ height: "56px" }}
              >
                Descartar
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Stack>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderBillingReminders = () => (
    <Stack spacing={2}>
      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Recordatorio manual
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Cliente</InputLabel>
              <Select
                label="Cliente"
                value={billingReminderForm.customerId || ""}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    customerId: event.target.value,
                  })
                }
              >
                <MenuItem value="">Selecciona cliente</MenuItem>
                {catalog.customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Servicio</InputLabel>
              <Select
                label="Servicio"
                value={billingReminderForm.itemId || ""}
                onChange={(event) =>
                  selectBillingReminderService(event.target.value)
                }
              >
                <MenuItem value="">Manual</MenuItem>
                {catalog.items
                  .filter((item) => item.type === "service")
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          {!billingReminderForm.itemId && (
            <Grid item xs={12} md={3}>
              <TextField
                label="Nombre servicio"
                fullWidth
                value={billingReminderForm.itemName || ""}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    itemName: event.target.value,
                  })
                }
              />
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Periodo</InputLabel>
              <Select
                label="Periodo"
                value={billingReminderForm.period || "yearly"}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    period: event.target.value as InvoiceBillingPeriod,
                  })
                }
              >
                <MenuItem value="monthly">Mensual</MenuItem>
                <MenuItem value="quarterly">Trimestral</MenuItem>
                <MenuItem value="yearly">Anual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Renovacíon"
              type="date"
              fullWidth
              value={billingReminderForm.renewalDate || ""}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  renewalDate: event.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Cantidad"
              type="number"
              fullWidth
              value={billingReminderForm.quantity || 1}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  quantity: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Valor"
              type="number"
              fullWidth
              value={billingReminderForm.unitPrice || 0}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  unitPrice: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Notas"
              fullWidth
              value={billingReminderForm.notes || ""}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  notes: event.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={editingBillingReminderId ? <Save /> : <Add />}
              disabled={saving}
              onClick={submitBillingReminder}
              sx={{ height: "56px" }}
            >
              {editingBillingReminderId ? "Guardar" : "Crear"}
            </Button>
          </Grid>
          {editingBillingReminderId && (
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Cancel />}
                onClick={resetBillingReminderForm}
                sx={{ height: "56px" }}
              >
                Cancelar
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={panelSx}>
        {renderReminderFilters()}
        {renderReminderMetrics()}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Servicio</TableCell>
                <TableCell>Aviso</TableCell>
                <TableCell>Renovacíon</TableCell>
                <TableCell>Periodo</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Factura origen</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBillingReminders.map((reminder) => (
                <TableRow key={reminder.id}>
                  <TableCell>{reminder.customerSnapshot.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {reminder.itemName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reminder.notes || reminder.itemDescription}
                    </Typography>
                  </TableCell>
                  <TableCell>{dateLabel(reminder.reminderDate)}</TableCell>
                  <TableCell>{dateLabel(reminder.renewalDate)}</TableCell>
                  <TableCell>{periodLabel[reminder.period]}</TableCell>
                  <TableCell align="right">{money(reminder.total)}</TableCell>
                  <TableCell>
                    {reminder.sourceInvoiceNumber || "-"}
                    {reminder.billedInvoiceNumber
                      ? ` -> ${reminder.billedInvoiceNumber}`
                      : ""}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={reminderStatusColor(reminder.status) as any}
                      label={reminderStatusLabel[reminder.status]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => editBillingReminder(reminder)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {reminder.status === "pending" && (
                      <>
                        <Tooltip title="Preparar factura">
                          <IconButton
                            size="small"
                            disabled={saving}
                            onClick={() => prepareSaleFromBillingReminder(reminder)}
                          >
                            <EventRepeat fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar renovación">
                          <IconButton
                            size="small"
                            disabled={saving}
                            onClick={() => cancelBillingReminder(reminder.id)}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        disabled={saving}
                        onClick={() => deleteBillingReminder(reminder.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderSales = () => (
    <Box sx={panelSx}>
      {renderSalesFilters(true)}
      {renderSummaryMetrics()}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Factura</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Vence</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Abonado</TableCell>
              <TableCell align="right">Saldo</TableCell>
              <TableCell align="right">PDFs</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSales.map((sale) => (
              <TableRow key={sale.id}>
                <TableCell>{sale.invoiceNumber}</TableCell>
                <TableCell>{sale.customerSnapshot.name}</TableCell>
                <TableCell>{dateLabel(sale.issuedAt)}</TableCell>
                <TableCell>{dateLabel(sale.dueDate)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={statusColor(sale.status) as any}
                    label={statusLabel[sale.status] || sale.status}
                  />
                </TableCell>
                <TableCell align="right">{money(sale.total)}</TableCell>
                <TableCell align="right">{money(salePaidTotal(sale))}</TableCell>
                <TableCell align="right">{money(saleBalance(sale))}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {sale.pdfUrl && (
                      <Tooltip title="Factura CC">
                        <IconButton
                          size="small"
                          component="a"
                          href={sale.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {sale.lrReports.map((report) => (
                      <Tooltip
                        key={report.sellerId}
                        title={`${report.lrNumber || "LR"} ${report.sellerName}`}
                      >
                        <IconButton
                          size="small"
                          component="a"
                          href={report.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Detalle y abonos">
                    <IconButton
                      size="small"
                      onClick={() => openSaleDialog(sale)}
                    >
                      <Payments fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {sale.status !== "void" && (
                    <Tooltip title="Anular">
                      <IconButton size="small" onClick={() => voidSale(sale.id)}>
                        <Block fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderAnalytics = () => (
    <Stack spacing={2}>
      <Box sx={panelSx}>
        {renderSalesFilters(false)}
        {renderSummaryMetrics()}
      </Box>

      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Ventas por día
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Facturas</TableCell>
                <TableCell align="right">Vendido</TableCell>
                <TableCell align="right">Abonado</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell align="right">Ganancia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salesSummary.daily.map((entry) => (
                <TableRow key={entry.date}>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell align="right">{entry.invoiceCount}</TableCell>
                  <TableCell align="right">{money(entry.totalSales)}</TableCell>
                  <TableCell align="right">{money(entry.paidTotal)}</TableCell>
                  <TableCell align="right">{money(entry.balanceTotal)}</TableCell>
                  <TableCell align="right">{money(entry.grossProfit)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Box sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Por vendedor
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vendedor</TableCell>
                    <TableCell align="right">Facturas</TableCell>
                    <TableCell align="right">Vendido</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                    <TableCell align="right">Ganancia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesSummary.sellers.slice(0, 10).map((seller) => (
                    <TableRow key={seller.sellerId}>
                      <TableCell>{seller.sellerName}</TableCell>
                      <TableCell align="right">{seller.invoiceCount}</TableCell>
                      <TableCell align="right">{money(seller.totalSales)}</TableCell>
                      <TableCell align="right">
                        {money(seller.commissionTotal)}
                      </TableCell>
                      <TableCell align="right">{money(seller.grossProfit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box sx={panelSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Por ítem
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ítem</TableCell>
                    <TableCell>Categoría</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Vendido</TableCell>
                    <TableCell align="right">Ganancia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesSummary.items.slice(0, 10).map((item) => (
                    <TableRow key={item.itemId || item.itemName}>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.categoryName || "-"}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{money(item.totalSales)}</TableCell>
                      <TableCell align="right">{money(item.grossProfit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );

  const renderSaleDialog = () => (
    <Dialog
      open={Boolean(selectedSale)}
      onClose={closeSaleDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>{selectedSale?.invoiceNumber}</DialogTitle>
      <DialogContent sx={{ bgcolor: "background.paper" }}>
        {selectedSale && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {selectedSale.customerSnapshot.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {money(selectedSale.total)} · Saldo {money(saleBalance(selectedSale))}
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ítem</TableCell>
                  <TableCell align="right">Cant.</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell align="right">Comisión</TableCell>
                  <TableCell align="right">Ganancia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSale.items.map((item) => (
                  <TableRow key={item.lineId}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{money(item.subtotal)}</TableCell>
                    <TableCell align="right">{money(item.totalCost)}</TableCell>
                    <TableCell align="right">
                      {item.sellerName ? `${item.sellerName} · ` : ""}
                      {money(item.commissionAmount)}
                    </TableCell>
                    <TableCell align="right">
                      {money(item.subtotal - item.totalCost - item.commissionAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Abonos
            </Typography>
            {selectedSale.payments.length === 0 ? (
              <Alert severity="info">Sin abonos registrados</Alert>
            ) : (
              selectedSale.payments.map((payment) => (
                <Box
                  key={payment.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {money(payment.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateLabel(payment.paidAt)} {payment.method || ""}
                    </Typography>
                  </Box>
                  {selectedSale.status !== "void" && (
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const result = await deletePayment(
                          selectedSale.id,
                          payment.id
                        );
                        if (result) {
                          setSelectedSale(result);
                          setPaymentForm((current) => ({
                            ...current,
                            amount: saleBalance(result),
                          }));
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))
            )}
            {selectedSale.status !== "void" && saleBalance(selectedSale) > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Abono"
                    type="number"
                    fullWidth
                    value={paymentForm.amount || 0}
                    inputProps={{ min: 0, max: saleBalance(selectedSale) }}
                    helperText={`Saldo: ${money(saleBalance(selectedSale))}`}
                    onChange={(event) =>
                      setPaymentForm({
                        ...paymentForm,
                        amount: numberValue(event.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Método"
                    fullWidth
                    value={paymentForm.method || ""}
                    onChange={(event) =>
                      setPaymentForm({
                        ...paymentForm,
                        method: event.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Notas"
                    fullWidth
                    value={paymentForm.notes || ""}
                    onChange={(event) =>
                      setPaymentForm({
                        ...paymentForm,
                        notes: event.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeSaleDialog}>Cerrar</Button>
        {selectedSale && selectedSale.status !== "void" && saleBalance(selectedSale) > 0 && (
          <>
            <Button
              variant="outlined"
              disabled={saving}
              onClick={() => registerSelectedSalePayment(saleBalance(selectedSale))}
            >
              Pagar saldo
            </Button>
            <Button
              variant="contained"
              disabled={saving || Number(paymentForm.amount || 0) <= 0}
              onClick={() => registerSelectedSalePayment()}
            >
              Registrar abono
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderQuotePreviewDialog = () => (
    <Dialog
      open={quotePreviewOpen}
      onClose={() => setQuotePreviewOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>Borrador de cotización</DialogTitle>
      <DialogContent sx={{ bgcolor: "background.paper" }}>
        <Box
          component="iframe"
          title="Borrador de cotización"
          srcDoc={quotePreviewHtml}
          sx={{
            width: "100%",
            height: { xs: "70vh", md: "78vh" },
            border: 0,
            bgcolor: "background.default",
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setQuotePreviewOpen(false)}>Cerrar</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={submitQuote}
          startIcon={<PictureAsPdf />}
        >
          Generar cotización
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCategoryDialog = () => (
    <Dialog
      open={categoryDialogOpen}
      onClose={closeCategoryDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>
        {editingCategoryId ? "Editar categoría" : "Nueva categoría"}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Nombre"
            fullWidth
            value={categoryForm.name || ""}
            onChange={(event) =>
              setCategoryForm({ ...categoryForm, name: event.target.value })
            }
          />
          <TextField
            label="Descripción"
            fullWidth
            multiline
            rows={4}
            value={categoryForm.description || ""}
            onChange={(event) =>
              setCategoryForm({
                ...categoryForm,
                description: event.target.value,
              })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeCategoryDialog}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={requestCategorySave}
          disabled={saving}
          startIcon={editingCategoryId ? <Save /> : <Add />}
        >
          {editingCategoryId ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderItemDialog = () => (
    <Dialog
      open={itemDialogOpen}
      onClose={closeItemDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>
        {editingItemId ? "Editar producto o servicio" : "Nuevo producto o servicio"}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={itemForm.type || "service"}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    type: event.target.value as "product" | "service",
                    recurrence:
                      event.target.value === "service"
                        ? itemForm.recurrence || { period: "none" }
                        : { period: "none" },
                  })
                }
              >
                <MenuItem value="product">Producto</MenuItem>
                <MenuItem value="service">Servicio</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nombre"
              fullWidth
              value={itemForm.name || ""}
              onChange={(event) =>
                setItemForm({ ...itemForm, name: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría"
                value={itemForm.categoryId || ""}
                onChange={(event) =>
                  setItemForm({ ...itemForm, categoryId: event.target.value })
                }
              >
                <MenuItem value="">Sin categoría</MenuItem>
                {catalog.categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={2}
              value={itemForm.description || ""}
              onChange={(event) =>
                setItemForm({ ...itemForm, description: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Precio"
              type="number"
              fullWidth
              value={itemForm.unitPrice || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  unitPrice: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Costo"
              type="number"
              fullWidth
              value={itemForm.cost || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  cost: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(itemForm.isVariablePrice)}
                  onChange={(event) =>
                    setItemForm({
                      ...itemForm,
                      isVariablePrice: event.target.checked,
                    })
                  }
                />
              }
              label="Precio variable"
            />
          </Grid>
          {itemForm.type !== "product" && (
            <>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(
                        itemForm.recurrence?.period &&
                          itemForm.recurrence.period !== "none"
                      )}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          recurrence: {
                            period: event.target.checked ? "yearly" : "none",
                          },
                        })
                      }
                    />
                  }
                  label="Servicio recurrente"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Periodicidad</InputLabel>
                  <Select
                    label="Periodicidad"
                    value={itemForm.recurrence?.period || "none"}
                    onChange={(event) =>
                      setItemForm({
                        ...itemForm,
                        recurrence: {
                          period: event.target.value as InvoiceBillingPeriod,
                        },
                      })
                    }
                  >
                    <MenuItem value="none">No recurrente</MenuItem>
                    <MenuItem value="monthly">Mensual</MenuItem>
                    <MenuItem value="quarterly">Trimestral</MenuItem>
                    <MenuItem value="yearly">Anual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {canUseLyproBilling ? (
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(itemForm.usagePricing?.enabled)}
                        onChange={(event) =>
                          setItemForm({
                            ...itemForm,
                            isVariablePrice:
                              event.target.checked || itemForm.isVariablePrice,
                            recurrence: event.target.checked
                              ? {
                                  period:
                                    itemForm.recurrence?.period &&
                                    itemForm.recurrence.period !== "none"
                                      ? itemForm.recurrence.period
                                      : "monthly",
                                }
                              : itemForm.recurrence,
                            usagePricing: {
                              ...(itemForm.usagePricing || usagePricingDefaults),
                              enabled: event.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Cobro LYPRO"
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      minHeight: 56,
                      display: "flex",
                      alignItems: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">
                      Cobro LYPRO solo disponible para Prueba-001.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {lyproUsagePricingEnabled && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Precio chat"
                      type="number"
                      fullWidth
                      value={itemForm.usagePricing?.pricePerChat || 0}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerChat: numberValue(event.target.value),
                          },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Precio mensaje"
                      type="number"
                      fullWidth
                      value={itemForm.usagePricing?.pricePerMessage || 0}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerMessage: numberValue(event.target.value),
                          },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Precio 1K tokens"
                      type="number"
                      fullWidth
                      value={itemForm.usagePricing?.pricePerThousandTokens || 0}
                      onChange={(event) =>
                        setItemForm({
                          ...itemForm,
                          usagePricing: {
                            ...(itemForm.usagePricing || usagePricingDefaults),
                            pricePerThousandTokens: numberValue(
                              event.target.value
                            ),
                          },
                        })
                      }
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Comisión</InputLabel>
              <Select
                label="Comisión"
                value={itemForm.commissionRule?.type || "none"}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    commissionRule: {
                      ...(itemForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      type: event.target.value as InvoiceCommissionType,
                    },
                  })
                }
              >
                <MenuItem value="none">Sin comision</MenuItem>
                <MenuItem value="percentage">Porcentaje</MenuItem>
                <MenuItem value="fixed">Valor fijo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Valor comision"
              type="number"
              fullWidth
              value={itemForm.commissionRule?.value || 0}
              onChange={(event) =>
                setItemForm({
                  ...itemForm,
                  commissionRule: {
                    ...(itemForm.commissionRule || {
                      type: "none",
                      value: 0,
                    }),
                    value: numberValue(event.target.value),
                  },
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Vendedor fijo</InputLabel>
              <Select
                label="Vendedor fijo"
                value={itemForm.commissionRule?.sellerId || ""}
                onChange={(event) =>
                  setItemForm({
                    ...itemForm,
                    commissionRule: {
                      ...(itemForm.commissionRule || {
                        type: "none",
                        value: 0,
                      }),
                      sellerId: event.target.value,
                    },
                  })
                }
              >
                <MenuItem value="">Sin vendedor fijo</MenuItem>
                {catalog.sellers.map((seller) => (
                  <MenuItem key={seller.id} value={seller.id}>
                    {seller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeItemDialog}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={requestItemSave}
          disabled={saving}
          startIcon={editingItemId ? <Save /> : <Add />}
        >
          {editingItemId ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPeopleDialog = (
    kind: "customer" | "seller",
    open: boolean,
    onClose: () => void,
    title: string
  ) => (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={kind === "customer" ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
        {kind === "customer"
          ? renderPeopleForm(
              kind,
              customerForm,
              editingCustomerId,
              setCustomerForm,
              requestCustomerSave,
              onClose,
              false,
              true
            )
          : renderPeopleForm(
              kind,
              sellerForm,
              editingSellerId,
              setSellerForm,
              requestSellerSave,
              onClose,
              false,
              true
            )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={editingCustomerId || editingSellerId ? <Save /> : <Add />}
          disabled={saving}
          onClick={kind === "customer" ? requestCustomerSave : requestSellerSave}
        >
          {editingCustomerId || editingSellerId ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCategoriesView = () => {
    const filteredRows = catalog.categories.filter((category) =>
      [category.name, category.description].some((value) =>
        matchesSearchText(value, categorySearch.trim())
      )
    );
    const rows = paginateRows(
      sortTableRows(filteredRows, categorySort, {
        name: (category) => category.name || "",
        description: (category) => category.description || "",
      }),
      categoryTablePagination.page,
      categoryTablePagination.rowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Categorías
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Separa los grupos de productos y servicios.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Buscar"
                size="small"
                value={categorySearch}
                onChange={(event) => {
                  setCategorySearch(event.target.value);
                  categoryTablePagination.setPage(0);
                }}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCategoryCreateDialog}
              >
                Nueva categoría
              </Button>
            </Stack>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell(
                    "Nombre",
                    "name",
                    categorySort,
                    (column) => {
                      setCategorySort((current) =>
                        current.orderBy === column
                          ? {
                              orderBy: current.orderBy,
                              direction: current.direction === "asc" ? "desc" : "asc",
                            }
                          : { orderBy: column, direction: "asc" }
                      );
                      categoryTablePagination.setPage(0);
                    }
                  )}
                  {renderSortableHeaderCell(
                    "Descripción",
                    "description",
                    categorySort,
                    (column) => {
                      setCategorySort((current) =>
                        current.orderBy === column
                          ? {
                              orderBy: current.orderBy,
                              direction: current.direction === "asc" ? "desc" : "asc",
                            }
                          : { orderBy: column, direction: "asc" }
                      );
                      categoryTablePagination.setPage(0);
                    }
                  )}
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>{category.description}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openCategoryEditDialog(category)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => requestCategoryDelete(category.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={categoryTablePagination.page}
            onPageChange={(_event, page) => categoryTablePagination.setPage(page)}
            rowsPerPage={categoryTablePagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              categoryTablePagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
        {renderCategoryDialog()}
      </Stack>
    );
  };

  const renderProductsView = () => {
    const filteredRows = catalog.items.filter((item) =>
      [item.name, item.description, item.categoryName, item.type].some((value) =>
        matchesSearchText(value, itemSearch.trim())
      )
    );
    const rows = paginateRows(
      sortTableRows(filteredRows, itemSort, {
        name: (item) => item.name || "",
        category: (item) => item.categoryName || "",
        type: (item) => item.type || "",
        period: (item) => item.recurrence?.period || "none",
        price: (item) => Number(item.unitPrice || 0),
        cost: (item) => Number(item.cost || 0),
      }),
      itemTablePagination.page,
      itemTablePagination.rowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Productos y servicios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona precios, costos, recurrencias y comisiones.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Buscar"
                size="small"
                value={itemSearch}
                onChange={(event) => {
                  setItemSearch(event.target.value);
                  itemTablePagination.setPage(0);
                }}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openItemCreateDialog}
              >
                Nuevo producto/servicio
              </Button>
            </Stack>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Ítem", "name" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Categoría", "category" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Tipo", "type" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Periodicidad", "period" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Precio", "price" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Costo", "cost" as any, itemSort as any, (column) => {
                    setItemSort((current) =>
                      current.orderBy === column
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column, direction: "asc" }
                    );
                    itemTablePagination.setPage(0);
                  }, "right")}
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                      {item.usagePricing?.enabled && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip size="small" label="LYPRO consumo" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{item.categoryName || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.type === "product" ? "Producto" : "Servicio"}
                      />
                    </TableCell>
                    <TableCell>
                      {periodLabel[item.recurrence?.period || "none"]}
                    </TableCell>
                    <TableCell align="right">
                      {item.isVariablePrice ? "Variable" : money(item.unitPrice)}
                    </TableCell>
                    <TableCell align="right">{money(item.cost)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openItemEditDialog(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => requestItemDelete(item.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={itemTablePagination.page}
            onPageChange={(_event, page) => itemTablePagination.setPage(page)}
            rowsPerPage={itemTablePagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              itemTablePagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
        {renderItemDialog()}
      </Stack>
    );
  };

  const renderCustomerDialog = () =>
    renderPeopleDialog(
      "customer",
      customerDialogOpen,
      closeCustomerDialog,
      editingCustomerId ? "Editar cliente" : "Nuevo cliente"
    );

  const renderSellerDialog = () =>
    renderPeopleDialog(
      "seller",
      sellerDialogOpen,
      closeSellerDialog,
      editingSellerId ? "Editar vendedor" : "Nuevo vendedor"
    );

  const renderPeopleView = (
    kind: "customer" | "seller",
    rows: Array<InvoiceCustomerModel | InvoiceSellerModel>,
    pagination: ReturnType<typeof useTablePagination>,
    onCreate: () => void,
    onEdit: (row: any) => void,
    onDelete: (id: string) => void,
    dialog: React.ReactNode
  ) => {
    const searchQuery = kind === "customer" ? customerSearch : sellerSearch;
    const sort = kind === "customer" ? customerSort : sellerSort;
    const filteredRows = rows.filter((row) => {
      const contactValue = `${row.phone || ""} ${row.email || ""}`.trim();
      const documentValue =
        "documentType" in row && row.documentType
          ? `${row.documentType} ${row.documentNumber || ""}`
          : row.documentNumber || "";
      const extraValues =
        kind === "customer"
          ? [
              (row as InvoiceCustomerModel).address,
              lyproCompanyLabel((row as InvoiceCustomerModel).lyproCompanyId),
            ]
          : [];

      return [
        row.name,
        documentValue,
        contactValue,
        row.notes,
        ...extraValues,
      ].some((value) => matchesSearchText(value, searchQuery.trim()));
    });
    const sortedRows = sortTableRows(filteredRows, sort as any, {
      name: (row: InvoiceCustomerModel | InvoiceSellerModel) => row.name || "",
      document: (row: InvoiceCustomerModel | InvoiceSellerModel) =>
        "documentType" in row && row.documentType
          ? `${row.documentType} ${row.documentNumber || ""}`
          : row.documentNumber || "",
      contact: (row: InvoiceCustomerModel | InvoiceSellerModel) =>
        `${row.phone || ""} ${row.email || ""}`.trim(),
      address: (row: InvoiceCustomerModel | InvoiceSellerModel) =>
        kind === "customer" ? (row as InvoiceCustomerModel).address || "" : "",
      lypro: (row: InvoiceCustomerModel | InvoiceSellerModel) =>
        kind === "customer"
          ? lyproCompanyLabel((row as InvoiceCustomerModel).lyproCompanyId) || ""
          : "",
      notes: (row: InvoiceCustomerModel | InvoiceSellerModel) => row.notes || "",
    } as any);
    const paginatedRows = paginateRows(
      sortedRows,
      pagination.page,
      pagination.rowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {kind === "customer" ? "Clientes" : "Vendedores"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {kind === "customer"
                  ? "Clientes con su compañía LYPRO y datos de contacto."
                  : "Vendedores, comisiones y datos de seguimiento."}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                label="Buscar"
                size="small"
                value={searchQuery}
                onChange={(event) => {
                  if (kind === "customer") {
                    setCustomerSearch(event.target.value);
                  } else {
                    setSellerSearch(event.target.value);
                  }
                  pagination.setPage(0);
                }}
                sx={{ minWidth: 240 }}
              />
              <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
                {kind === "customer" ? "Nuevo cliente" : "Nuevo vendedor"}
              </Button>
            </Stack>
          </Stack>
          {renderPeopleTable(
            kind,
            paginatedRows,
            onEdit,
            onDelete,
            sort as any,
            (column) => {
              if (kind === "customer") {
                setCustomerSort((current) =>
                  current.orderBy === (column as any)
                    ? {
                        orderBy: current.orderBy,
                        direction: current.direction === "asc" ? "desc" : "asc",
                      }
                    : { orderBy: column as any, direction: "asc" }
                );
              } else {
                setSellerSort((current) =>
                  current.orderBy === (column as any)
                    ? {
                        orderBy: current.orderBy,
                        direction: current.direction === "asc" ? "desc" : "asc",
                      }
                    : { orderBy: column as any, direction: "asc" }
                );
              }
              pagination.setPage(0);
            }
          )}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={pagination.page}
            onPageChange={(_event, page) => pagination.setPage(page)}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              pagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
        {dialog}
      </Stack>
    );
  };

  const renderBillingReminderDialog = () => (
    <Dialog
      open={billingReminderDialogOpen}
      onClose={closeBillingReminderDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: billingDialogPaperSx,
      }}
    >
      <DialogTitle>
        {editingBillingReminderId ? "Editar recordatorio" : "Nuevo recordatorio"}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Cliente</InputLabel>
              <Select
                label="Cliente"
                value={billingReminderForm.customerId || ""}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    customerId: event.target.value,
                  })
                }
              >
                <MenuItem value="">Selecciona cliente</MenuItem>
                {catalog.customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Servicio</InputLabel>
              <Select
                label="Servicio"
                value={billingReminderForm.itemId || ""}
                onChange={(event) => selectBillingReminderService(event.target.value)}
              >
                <MenuItem value="">Manual</MenuItem>
                {catalog.items
                  .filter((item) => item.type === "service")
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          {!billingReminderForm.itemId && (
            <Grid item xs={12}>
              <TextField
                label="Nombre servicio"
                fullWidth
                value={billingReminderForm.itemName || ""}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    itemName: event.target.value,
                  })
                }
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Periodo</InputLabel>
              <Select
                label="Periodo"
                value={billingReminderForm.period || "yearly"}
                onChange={(event) =>
                  setBillingReminderForm({
                    ...billingReminderForm,
                    period: event.target.value as InvoiceBillingPeriod,
                  })
                }
              >
                <MenuItem value="monthly">Mensual</MenuItem>
                <MenuItem value="quarterly">Trimestral</MenuItem>
                <MenuItem value="yearly">Anual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Renovacíon"
              type="date"
              fullWidth
              value={billingReminderForm.renewalDate || ""}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  renewalDate: event.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Cantidad"
              type="number"
              fullWidth
              value={billingReminderForm.quantity || 1}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  quantity: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Valor"
              type="number"
              fullWidth
              value={billingReminderForm.unitPrice || 0}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  unitPrice: numberValue(event.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notas"
              fullWidth
              value={billingReminderForm.notes || ""}
              onChange={(event) =>
                setBillingReminderForm({
                  ...billingReminderForm,
                  notes: event.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeBillingReminderDialog}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={requestBillingReminderSave}
          disabled={saving}
          startIcon={editingBillingReminderId ? <Save /> : <Add />}
        >
          {editingBillingReminderId ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderQuotesView = () => {
    const manualCustomer: Partial<InvoiceCustomerModel> =
      quoteForm.customer || {};
    const canSubmitQuote =
      quoteForm.description.trim().length > 0 &&
      Number(quoteForm.total || 0) > 0 &&
      Boolean(quoteForm.customerId || manualCustomer.name);

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Cotizaciones
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Genera cotizaciónes sin mezclar el historial.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RequestQuote />}
              onClick={() => navigate(VIEW_PATHS["quote-history"])}
            >
              Ver historial
            </Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={quoteForm.customerId || ""}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      customerId: event.target.value,
                    })
                  }
                >
                  <MenuItem value="">Cliente manual</MenuItem>
                  {catalog.customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!quoteForm.customerId && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Nombre cliente"
                    fullWidth
                    value={manualCustomer.name || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          name: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Documento"
                    fullWidth
                    value={manualCustomer.documentNumber || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          documentNumber: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Teléfono"
                    fullWidth
                    value={manualCustomer.phone || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          phone: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Correo"
                    fullWidth
                    value={manualCustomer.email || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          email: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Dirección"
                    fullWidth
                    value={manualCustomer.address || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          address: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                label="Fecha"
                type="date"
                fullWidth
                value={quoteForm.issuedAt || ""}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    issuedAt: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: defaultIssueDate() }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select
                  label="Moneda"
                  value={quoteForm.currency}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      currency: event.target.value as InvoiceQuoteCurrency,
                    })
                  }
                >
                  <MenuItem value="COP">COP</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Total"
                type="number"
                fullWidth
                value={quoteForm.total}
                inputProps={{
                  min: 0,
                  step: quoteForm.currency === "USD" ? "0.01" : "1",
                }}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    total: numberValue(event.target.value),
                  })
                }
                helperText={quoteMoney(quoteForm.total, quoteForm.currency)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={6}
                value={quoteForm.description}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    description: event.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Visibility />}
                disabled={saving || !canSubmitQuote}
                onClick={previewQuoteDraft}
                sx={{ height: "56px" }}
              >
                Previsualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdf />}
                disabled={saving || !canSubmitQuote}
                onClick={requestQuoteSubmission}
                sx={{ height: "56px" }}
              >
                Generar cotización
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    );
  };

  const renderQuoteCreateDialog = () => {
    const manualCustomer: Partial<InvoiceCustomerModel> =
      quoteForm.customer || {};
    const canSubmitQuote =
      quoteForm.description.trim().length > 0 &&
      Number(quoteForm.total || 0) > 0 &&
      Boolean(quoteForm.customerId || manualCustomer.name);

    return (
      <Dialog
        open={quoteFormDialogOpen}
        onClose={saving ? undefined : closeQuoteCreateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: billingDialogPaperSx }}
      >
        <DialogTitle>Crear cotización</DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "background.paper",
            p: { xs: 1.5, sm: 2 },
            maxHeight: { xs: "calc(100dvh - 180px)", md: "calc(100dvh - 190px)" },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={quoteForm.customerId || ""}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      customerId: event.target.value,
                    })
                  }
                >
                  <MenuItem value="">Cliente manual</MenuItem>
                  {catalog.customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!quoteForm.customerId && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Nombre cliente"
                    fullWidth
                    value={manualCustomer.name || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          name: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Documento"
                    fullWidth
                    value={manualCustomer.documentNumber || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          documentNumber: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Teléfono"
                    fullWidth
                    value={manualCustomer.phone || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          phone: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Correo"
                    fullWidth
                    value={manualCustomer.email || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          email: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Dirección"
                    fullWidth
                    value={manualCustomer.address || ""}
                    onChange={(event) =>
                      setQuoteForm({
                        ...quoteForm,
                        customer: {
                          ...manualCustomer,
                          address: event.target.value,
                        },
                      })
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                label="Fecha"
                type="date"
                fullWidth
                value={quoteForm.issuedAt || ""}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    issuedAt: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: defaultIssueDate() }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select
                  label="Moneda"
                  value={quoteForm.currency}
                  onChange={(event) =>
                    setQuoteForm({
                      ...quoteForm,
                      currency: event.target.value as InvoiceQuoteCurrency,
                    })
                  }
                >
                  <MenuItem value="COP">COP</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Total"
                type="number"
                fullWidth
                value={quoteForm.total}
                inputProps={{
                  min: 0,
                  step: quoteForm.currency === "USD" ? "0.01" : "1",
                }}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    total: numberValue(event.target.value),
                  })
                }
                helperText={quoteMoney(quoteForm.total, quoteForm.currency)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={6}
                value={quoteForm.description}
                onChange={(event) =>
                  setQuoteForm({
                    ...quoteForm,
                    description: event.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button color="inherit" disabled={saving} onClick={closeQuoteCreateDialog}>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            disabled={saving || !canSubmitQuote}
            onClick={previewQuoteDraft}
          >
            Previsualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            disabled={saving || !canSubmitQuote}
            onClick={requestQuoteSubmission}
          >
            Generar cotización
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderQuoteHistoryView = () => {
    const rows = paginateRows(
      sortTableRows(filteredQuoteHistory, quoteHistorySort as any, {
        quoteNumber: (quote: InvoiceQuoteModel) => quote.quoteNumber || "",
        customer: (quote: InvoiceQuoteModel) => quote.customerSnapshot.name || "",
        date: (quote: InvoiceQuoteModel) => quote.issuedAt || "",
        description: (quote: InvoiceQuoteModel) => quote.description || "",
        currency: (quote: InvoiceQuoteModel) => quote.currency || "",
        total: (quote: InvoiceQuoteModel) => Number(quote.total || 0),
      } as any),
      quoteHistoryPagination.page,
      quoteHistoryPagination.rowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Historial de cotizaciónes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Historial independiente para revisar, filtrar y reabrir PDFs.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                label="Buscar"
                size="small"
                value={quoteHistorySearch}
                onChange={(event) => {
                  setQuoteHistorySearch(event.target.value);
                  quoteHistoryPagination.setPage(0);
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openQuoteCreateDialog}
              >
                Crear cotización
              </Button>
            </Stack>
          </Stack>
          {quotesLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Actualizando historial...
              </Typography>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Cotización", "quoteNumber" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Cliente", "customer" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Fecha", "date" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Descripción", "description" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Moneda", "currency" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Total", "total" as any, quoteHistorySort as any, (column) => {
                    setQuoteHistorySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    quoteHistoryPagination.setPage(0);
                  }, "right")}
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell>{quote.quoteNumber}</TableCell>
                    <TableCell>{quote.customerSnapshot.name}</TableCell>
                    <TableCell>{dateLabel(quote.issuedAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {quote.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{quote.currency}</TableCell>
                    <TableCell align="right">
                      {quoteMoney(quote.total, quote.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {quote.pdfUrl && (
                        <Tooltip title="Cotización PDF">
                          <IconButton
                            size="small"
                            component="a"
                            href={quote.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredQuoteHistory.length}
            page={quoteHistoryPagination.page}
            onPageChange={(_event, page) => quoteHistoryPagination.setPage(page)}
            rowsPerPage={quoteHistoryPagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              quoteHistoryPagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
      </Stack>
    );
  };

  const renderCustomersView = () =>
    renderPeopleView(
      "customer",
      catalog.customers,
      customerTablePagination,
      openCustomerCreateDialog,
      openCustomerEditDialog,
      requestCustomerDelete,
      renderCustomerDialog()
    );

  const renderSellersView = () =>
    renderPeopleView(
      "seller",
      catalog.sellers,
      sellerTablePagination,
      openSellerCreateDialog,
      openSellerEditDialog,
      requestSellerDelete,
      renderSellerDialog()
    );

  const renderBillingRemindersView = () => {
    const reminderSortAccessors = {
      customer: (reminder: InvoiceBillingReminderModel) =>
        reminder.customerSnapshot.name || "",
      service: (reminder: InvoiceBillingReminderModel) =>
        reminder.itemName || "",
      reminder: (reminder: InvoiceBillingReminderModel) =>
        reminder.reminderDate || "",
      renewal: (reminder: InvoiceBillingReminderModel) =>
        reminder.renewalDate || "",
      period: (reminder: InvoiceBillingReminderModel) => reminder.period || "",
      total: (reminder: InvoiceBillingReminderModel) =>
        Number(reminder.total || 0),
      invoice: (reminder: InvoiceBillingReminderModel) =>
        `${reminder.sourceInvoiceNumber || ""} ${
          reminder.billedInvoiceNumber || ""
        }`.trim(),
      status: (reminder: InvoiceBillingReminderModel) =>
        reminder.status || "",
    } as any;
    const sortedRows = sortTableRows(
      filteredBillingReminders,
      reminderSort as any,
      reminderSortAccessors
    );
    const rows = paginateRows(
      sortedRows,
      reminderTablePagination.page,
      reminderTablePagination.rowsPerPage
    );
    const exportBillingReminders = () => {
      const exportRows = sortedRows.map((reminder) => ({
        Cliente: reminder.customerSnapshot.name || "",
        "Documento del cliente": formatCustomerDocument(reminder.customerSnapshot),
        "Notas del cliente": reminder.customerSnapshot.notes || "",
        Plan: reminder.itemName || "",
        "Monto del plan": reminder.subtotal || 0,
        "Fecha de aviso": dateLabel(reminder.reminderDate),
        "Fecha de suspensión": dateLabel(reminder.renewalDate),
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      worksheet["!cols"] = [
        { wch: 32 },
        { wch: 24 },
        { wch: 48 },
        { wch: 16 },
        { wch: 18 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Recordatorios");

      const fileName = `recordatorios_${dateInputValue(new Date())}.xlsx`;
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recordatorios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona renovaciónes y facturación recurrente.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={exportBillingReminders}
                disabled={!sortedRows.length}
              >
                Exportar Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openBillingReminderCreateDialog}
              >
                Nuevo recordatorio
              </Button>
            </Stack>
          </Stack>
          {billingRemindersLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Actualizando recordatorios...
              </Typography>
            </Box>
          )}
          {renderReminderFilters()}
          {renderReminderMetrics()}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Cliente", "customer" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Servicio", "service" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Aviso", "reminder" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Renovación", "renewal" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Periodo", "period" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Valor", "total" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Factura origen", "invoice" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Estado", "status" as any, reminderSort as any, (column) => {
                    setReminderSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    reminderTablePagination.setPage(0);
                  })}
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((reminder) => (
                  <TableRow key={reminder.id}>
                    <TableCell>{reminder.customerSnapshot.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {reminder.itemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reminder.notes || reminder.itemDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>{dateLabel(reminder.reminderDate)}</TableCell>
                    <TableCell>{dateLabel(reminder.renewalDate)}</TableCell>
                    <TableCell>{periodLabel[reminder.period]}</TableCell>
                    <TableCell align="right">{money(reminder.total)}</TableCell>
                    <TableCell>
                      {reminder.sourceInvoiceNumber || "-"}
                      {reminder.billedInvoiceNumber
                        ? ` -> ${reminder.billedInvoiceNumber}`
                        : ""}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={reminderStatusColor(reminder.status) as any}
                        label={reminderStatusLabel[reminder.status]}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => editBillingReminder(reminder)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {reminder.status === "pending" && (
                        <>
                          <Tooltip title="Preparar factura">
                            <IconButton
                              size="small"
                              disabled={saving}
                              onClick={() =>
                                openConfirmDialog({
                                  title: "Preparar factura",
                                  message:
                                    "¿Deseas preparar la factura desde este recordatorio?",
                                  confirmText: "Continuar",
                                  onConfirm: () => prepareSaleFromBillingReminder(reminder),
                                })
                              }
                            >
                              <EventRepeat fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar renovación">
                            <IconButton
                              size="small"
                              disabled={saving}
                              onClick={() =>
                                openConfirmDialog({
                                  title: "Cancelar renovación",
                                  message:
                                    "¿Deseas cancelar esta renovación programada?",
                                  confirmText: "Cancelar",
                                  confirmColor: "error",
                                  onConfirm: () => cancelBillingReminder(reminder.id),
                                })
                              }
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          disabled={saving}
                          onClick={() => requestBillingReminderDelete(reminder.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredBillingReminders.length}
            page={reminderTablePagination.page}
            onPageChange={(_event, page) => reminderTablePagination.setPage(page)}
            rowsPerPage={reminderTablePagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              reminderTablePagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
        {renderBillingReminderDialog()}
      </Stack>
    );
  };

  const renderSalesView = () => {
    const rows = paginateRows(
      sortTableRows(filteredSales, salesSort as any, {
        invoiceNumber: (sale: InvoiceSaleModel) => sale.invoiceNumber || "",
        customer: (sale: InvoiceSaleModel) => sale.customerSnapshot.name || "",
        issuedAt: (sale: InvoiceSaleModel) => sale.issuedAt || "",
        dueDate: (sale: InvoiceSaleModel) => sale.dueDate || "",
        status: (sale: InvoiceSaleModel) => sale.status || "",
        total: (sale: InvoiceSaleModel) => Number(sale.total || 0),
        paidTotal: salePaidTotal,
        balance: saleBalance,
      } as any),
      salesTablePagination.page,
      salesTablePagination.rowsPerPage
    );

    return (
      <Box sx={panelSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Facturas y cartera
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredSales.length} facturas en la vista actual
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddShoppingCart />}
            onClick={openNewSaleDialog}
            sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
          >
            Nueva venta
          </Button>
        </Stack>
        {renderSalesFilters(true)}
        {renderSummaryMetrics()}
        {salesLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Actualizando facturas...
            </Typography>
          </Box>
        )}
        <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Factura", "invoiceNumber" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Cliente", "customer" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Fecha", "issuedAt" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Vence", "dueDate" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Estado", "status" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Total", "total" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Abonado", "paidTotal" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Saldo", "balance" as any, salesSort as any, (column) => {
                    setSalesSort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    salesTablePagination.setPage(0);
                  }, "right")}
                  <TableCell align="right">PDFs</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((sale) => (
                <TableRow key={sale.id}>
                  <TableCell>{sale.invoiceNumber}</TableCell>
                  <TableCell>{sale.customerSnapshot.name}</TableCell>
                  <TableCell>{dateLabel(sale.issuedAt)}</TableCell>
                  <TableCell>{dateLabel(sale.dueDate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(sale.status) as any}
                      label={statusLabel[sale.status] || sale.status}
                    />
                  </TableCell>
                  <TableCell align="right">{money(sale.total)}</TableCell>
                  <TableCell align="right">{money(salePaidTotal(sale))}</TableCell>
                  <TableCell align="right">{money(saleBalance(sale))}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {sale.pdfUrl && (
                        <Tooltip title="Factura CC">
                          <IconButton
                            size="small"
                            component="a"
                            href={sale.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sale.lrReports.map((report) => (
                        <Tooltip
                          key={report.sellerId}
                          title={`${report.lrNumber || "LR"} ${report.sellerName}`}
                        >
                          <IconButton
                            size="small"
                            component="a"
                            href={report.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ))}
                      {sale.status !== "void" &&
                        (sale.pdfStatus === "pending" ||
                          !sale.pdfUrl ||
                          sale.lrReports.some((report) => !report.pdfUrl)) && (
                          <Tooltip title="Regenerar PDFs pendientes">
                            <IconButton
                              size="small"
                              onClick={() => regenerateSalePdfs(sale.id)}
                            >
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Detalle y abonos">
                      <IconButton size="small" onClick={() => openSaleDialog(sale)}>
                        <Payments fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {sale.status !== "void" && (
                      <Tooltip title="Anular">
                        <IconButton
                          size="small"
                          onClick={() =>
                            openConfirmDialog({
                              title: "Anular factura",
                              message:
                                "¿Deseas anular esta factura? Esta acción no se puede deshacer.",
                              confirmText: "Anular",
                              confirmColor: "error",
                              onConfirm: () => voidSale(sale.id),
                            })
                          }
                        >
                          <Block fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSales.length}
          page={salesTablePagination.page}
          onPageChange={(_event, page) => salesTablePagination.setPage(page)}
          rowsPerPage={salesTablePagination.rowsPerPage}
          onRowsPerPageChange={(event) =>
            salesTablePagination.setRowsPerPage(Number(event.target.value))
          }
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>
    );
  };

  const renderAnalyticsView = () => {
    const dailyRows = paginateRows(
      sortTableRows(salesSummary.daily, analyticsDailySort as any, {
        date: (entry: (typeof salesSummary.daily)[number]) => entry.date || "",
        invoiceCount: (entry: (typeof salesSummary.daily)[number]) =>
          Number(entry.invoiceCount || 0),
        totalSales: (entry: (typeof salesSummary.daily)[number]) =>
          Number(entry.totalSales || 0),
        paidTotal: (entry: (typeof salesSummary.daily)[number]) =>
          Number(entry.paidTotal || 0),
        balanceTotal: (entry: (typeof salesSummary.daily)[number]) =>
          Number(entry.balanceTotal || 0),
        grossProfit: (entry: (typeof salesSummary.daily)[number]) =>
          Number(entry.grossProfit || 0),
      } as any),
      analyticsDailyPagination.page,
      analyticsDailyPagination.rowsPerPage
    );
    const sellerRows = paginateRows(
      sortTableRows(salesSummary.sellers, analyticsSellerSort as any, {
        seller: (entry: (typeof salesSummary.sellers)[number]) =>
          entry.sellerName || "",
        invoiceCount: (entry: (typeof salesSummary.sellers)[number]) =>
          Number(entry.invoiceCount || 0),
        totalSales: (entry: (typeof salesSummary.sellers)[number]) =>
          Number(entry.totalSales || 0),
        commissionTotal: (entry: (typeof salesSummary.sellers)[number]) =>
          Number(entry.commissionTotal || 0),
        grossProfit: (entry: (typeof salesSummary.sellers)[number]) =>
          Number(entry.grossProfit || 0),
      } as any),
      analyticsSellerPagination.page,
      analyticsSellerPagination.rowsPerPage
    );
    const itemRows = paginateRows(
      sortTableRows(salesSummary.items, analyticsItemSort as any, {
        item: (entry: (typeof salesSummary.items)[number]) =>
          entry.itemName || "",
        category: (entry: (typeof salesSummary.items)[number]) =>
          entry.categoryName || "",
        quantity: (entry: (typeof salesSummary.items)[number]) =>
          Number(entry.quantity || 0),
        totalSales: (entry: (typeof salesSummary.items)[number]) =>
          Number(entry.totalSales || 0),
        grossProfit: (entry: (typeof salesSummary.items)[number]) =>
          Number(entry.grossProfit || 0),
      } as any),
      analyticsItemPagination.page,
      analyticsItemPagination.rowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Box sx={panelSx}>
          {renderSalesFilters(false)}
          {renderSummaryMetrics()}
          {salesLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Actualizando analíticas...
              </Typography>
            </Box>
          )}
        </Box>

            <Box sx={panelSx}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Ventas por día
              </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeaderCell("Fecha", "date" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  })}
                  {renderSortableHeaderCell("Facturas", "invoiceCount" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Vendido", "totalSales" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Abonado", "paidTotal" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Saldo", "balanceTotal" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  }, "right")}
                  {renderSortableHeaderCell("Ganancia", "grossProfit" as any, analyticsDailySort as any, (column) => {
                    setAnalyticsDailySort((current) =>
                      current.orderBy === (column as any)
                        ? {
                            orderBy: current.orderBy,
                            direction: current.direction === "asc" ? "desc" : "asc",
                          }
                        : { orderBy: column as any, direction: "asc" }
                    );
                    analyticsDailyPagination.setPage(0);
                  }, "right")}
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyRows.map((entry) => (
                  <TableRow key={entry.date}>
                    <TableCell>{entry.date}</TableCell>
                    <TableCell align="right">{entry.invoiceCount}</TableCell>
                    <TableCell align="right">{money(entry.totalSales)}</TableCell>
                    <TableCell align="right">{money(entry.paidTotal)}</TableCell>
                    <TableCell align="right">{money(entry.balanceTotal)}</TableCell>
                    <TableCell align="right">{money(entry.grossProfit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={salesSummary.daily.length}
            page={analyticsDailyPagination.page}
            onPageChange={(_event, page) => analyticsDailyPagination.setPage(page)}
            rowsPerPage={analyticsDailyPagination.rowsPerPage}
            onRowsPerPageChange={(event) =>
              analyticsDailyPagination.setRowsPerPage(Number(event.target.value))
            }
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={panelSx}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Por vendedor
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                      {renderSortableHeaderCell("Vendedor", "seller" as any, analyticsSellerSort as any, (column) => {
                        setAnalyticsSellerSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsSellerPagination.setPage(0);
                      })}
                      {renderSortableHeaderCell("Facturas", "invoiceCount" as any, analyticsSellerSort as any, (column) => {
                        setAnalyticsSellerSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsSellerPagination.setPage(0);
                      }, "right")}
                      {renderSortableHeaderCell("Vendido", "totalSales" as any, analyticsSellerSort as any, (column) => {
                        setAnalyticsSellerSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsSellerPagination.setPage(0);
                      }, "right")}
                      {renderSortableHeaderCell("Comisión", "commissionTotal" as any, analyticsSellerSort as any, (column) => {
                        setAnalyticsSellerSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsSellerPagination.setPage(0);
                      }, "right")}
                      {renderSortableHeaderCell("Ganancia", "grossProfit" as any, analyticsSellerSort as any, (column) => {
                        setAnalyticsSellerSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsSellerPagination.setPage(0);
                      }, "right")}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sellerRows.map((seller) => (
                      <TableRow key={seller.sellerId}>
                        <TableCell>{seller.sellerName}</TableCell>
                        <TableCell align="right">{seller.invoiceCount}</TableCell>
                        <TableCell align="right">{money(seller.totalSales)}</TableCell>
                        <TableCell align="right">{money(seller.commissionTotal)}</TableCell>
                        <TableCell align="right">{money(seller.grossProfit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={salesSummary.sellers.length}
                page={analyticsSellerPagination.page}
                onPageChange={(_event, page) => analyticsSellerPagination.setPage(page)}
                rowsPerPage={analyticsSellerPagination.rowsPerPage}
                onRowsPerPageChange={(event) =>
                  analyticsSellerPagination.setRowsPerPage(Number(event.target.value))
                }
                rowsPerPageOptions={[5, 10, 25]}
              />
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={panelSx}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Por ítem
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                      {renderSortableHeaderCell("Ítem", "item" as any, analyticsItemSort as any, (column) => {
                        setAnalyticsItemSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsItemPagination.setPage(0);
                      })}
                      {renderSortableHeaderCell("Categoría", "category" as any, analyticsItemSort as any, (column) => {
                        setAnalyticsItemSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsItemPagination.setPage(0);
                      })}
                      {renderSortableHeaderCell("Cantidad", "quantity" as any, analyticsItemSort as any, (column) => {
                        setAnalyticsItemSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsItemPagination.setPage(0);
                      }, "right")}
                      {renderSortableHeaderCell("Vendido", "totalSales" as any, analyticsItemSort as any, (column) => {
                        setAnalyticsItemSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsItemPagination.setPage(0);
                      }, "right")}
                      {renderSortableHeaderCell("Ganancia", "grossProfit" as any, analyticsItemSort as any, (column) => {
                        setAnalyticsItemSort((current) =>
                          current.orderBy === (column as any)
                            ? {
                                orderBy: current.orderBy,
                                direction: current.direction === "asc" ? "desc" : "asc",
                              }
                            : { orderBy: column as any, direction: "asc" }
                        );
                        analyticsItemPagination.setPage(0);
                      }, "right")}
                </TableRow>
              </TableHead>
                  <TableBody>
                    {itemRows.map((item) => (
                      <TableRow key={item.itemId || item.itemName}>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell>{item.categoryName || "-"}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{money(item.totalSales)}</TableCell>
                        <TableCell align="right">{money(item.grossProfit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={salesSummary.items.length}
                page={analyticsItemPagination.page}
                onPageChange={(_event, page) => analyticsItemPagination.setPage(page)}
                rowsPerPage={analyticsItemPagination.rowsPerPage}
                onRowsPerPageChange={(event) =>
                  analyticsItemPagination.setRowsPerPage(Number(event.target.value))
                }
                rowsPerPageOptions={[5, 10, 25]}
              />
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <PageLoadingState label="Cargando facturación" compact />;
    }
    if (view === "analytics") return renderAnalyticsView();
    if (view === "categories") return renderCategoriesView();
    if (view === "items") return renderProductsView();
    if (view === "customers") return renderCustomersView();
    if (view === "sellers") return renderSellersView();
    if (view === "reminders") return renderBillingRemindersView();
    if (view === "quotes") return renderQuoteHistoryView();
    if (view === "quote-history") return renderQuoteHistoryView();
    if (view === "new-sale") return renderNewSale();
    return renderSalesView();
  };

  return (
    <Box
      sx={{
        ...dashboardPageSx,
        ...dashboardPagePaddingSx,
        pb: 2,
      }}
    >
      {renderHeader()}
      {renderContent()}
      {renderQuoteCreateDialog()}
      <Dialog
        open={saleFormDialogOpen}
        onClose={saving ? undefined : closeNewSaleDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: billingDialogPaperSx }}
      >
        <DialogTitle>Nueva venta</DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "background.paper",
            p: { xs: 1.5, sm: 2 },
            maxHeight: { xs: "calc(100dvh - 180px)", md: "calc(100dvh - 190px)" },
          }}
        >
          {renderNewSale(true)}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            color="inherit"
            disabled={saving}
            onClick={closeNewSaleDialog}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            disabled={saving || saleLines.length === 0}
            onClick={submitSale}
          >
            Generar factura
          </Button>
        </DialogActions>
      </Dialog>
      {renderSaleDialog()}
      {renderQuotePreviewDialog()}
      <ConfirmationDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        message={confirmState?.message || ""}
        onClose={closeConfirmDialog}
        onConfirm={() => {
          const action = confirmState?.onConfirm;
          closeConfirmDialog();
          if (action) {
            void action();
          }
        }}
        confirmText={confirmState?.confirmText}
        confirmColor={confirmState?.confirmColor}
      />
      <FeedbackSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

const panelSx = {
  ...dashboardPanelSx,
  borderRadius: 2,
  overflow: "hidden",
};

const billingDialogPaperSx = {
  borderRadius: 1,
  bgcolor: "background.paper",
  backgroundImage: "none",
  border: "1px solid",
  borderColor: "divider",
} as const;

const peopleFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "transparent",
    borderRadius: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "divider",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "text.secondary",
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
  "& .MuiInputLabel-root": {
    color: "text.secondary",
  },
} as const;

export default InvoicesPage;




